<template>
  <div v-if="isActive" class="InventoryCircleAnalysis" @click.stop>
    <div v-if="!isZoomAllowed" class="InventoryCircleAnalysis__zoomNotice">
      {{ zoomNoticeMessage }}
    </div>
    <template v-else>
      <div v-if="isIndicator329" class="InventoryCircleAnalysis__filterHeader">
        <label class="InventoryCircleAnalysis__selectAllToggle">
          <input
            v-model="allMaintenance329Selected"
            type="checkbox"
            class="InventoryCircleAnalysis__classToggleInput"
          />
          <span class="InventoryCircleAnalysis__selectAllLabel">
            {{ t('myMap.inventoryCircle.selectAll') }}
          </span>
        </label>
      </div>
      <div v-if="aggregationEntries.length > 0" class="InventoryCircleAnalysis__bars">
        <div
          v-for="entry in aggregationEntries"
          :key="entry.typeLabel"
          class="InventoryCircleAnalysis__barItem"
        >
          <div class="InventoryCircleAnalysis__barLabel">
            <label
              v-if="isIndicator329 && isMaintenance329Bucket(entry.typeLabel)"
              class="InventoryCircleAnalysis__classToggle"
            >
              <input
                v-model="selectedMaintenance329Buckets[entry.typeLabel]"
                type="checkbox"
                class="InventoryCircleAnalysis__classToggleInput"
              />
            </label>
            <img
              v-if="isIndicator322 && typeOuvrageGaugeIconSrcByType[entry.typeLabel]"
              :src="typeOuvrageGaugeIconSrcByType[entry.typeLabel]"
              :alt="formatLabelNameForPanel(entry.typeLabel)"
              class="InventoryCircleAnalysis__barLabelIcon"
              loading="lazy"
            />
            <span class="InventoryCircleAnalysis__barLabelName">{{
              formatLabelNameForPanel(entry.typeLabel)
            }}</span>
            <span class="InventoryCircleAnalysis__barLabelValue">{{
              formatNumber(entry.count)
            }}</span>
          </div>
          <div class="InventoryCircleAnalysis__barContainer">
            <div
              class="InventoryCircleAnalysis__bar"
              :style="{
                width: `${(entry.count / maxCount) * 100}%`,
                backgroundColor: getColorForLegendLabel(entry.typeLabel),
              }"
            ></div>
          </div>
        </div>
      </div>
      <div
        v-if="isIndicator322"
        class="InventoryCircleAnalysis__typeLegend"
      >
        <div class="InventoryCircleAnalysis__typeLegendTitle">{{ t('myMap.inventoryCircle.statusLegend') }}</div>
        <div class="InventoryCircleAnalysis__statusLegend">
          <div
            v-for="entry in completion322StatusLegendEntries"
            :key="entry.label"
            class="InventoryCircleAnalysis__statusLegendItem"
          >
            <span
              class="InventoryCircleAnalysis__statusLegendDot"
              :style="{ backgroundColor: entry.color }"
            ></span>
            <span class="InventoryCircleAnalysis__statusLegendLabel">{{ entry.label }}</span>
          </div>
        </div>
      </div>
      <div
        v-if="isIndicator329 && maintenance329TypeLegendEntries.length > 0"
        class="InventoryCircleAnalysis__typeLegend"
      >
        <div class="InventoryCircleAnalysis__typeLegendTitle">{{ t('myMap.inventoryCircle.typeLegend') }}</div>
        <div class="InventoryCircleAnalysis__typeLegendGrid">
          <div
            v-for="entry in maintenance329TypeLegendEntries"
            :key="entry.label"
            class="InventoryCircleAnalysis__typeLegendItem"
          >
            <div class="InventoryCircleAnalysis__typeLegendHeader">{{ entry.label }}</div>
            <div class="InventoryCircleAnalysis__typeLegendStates">
              <div class="InventoryCircleAnalysis__typeLegendState">
                <img
                  :src="entry.iconSrc"
                  :alt="entry.label"
                  class="InventoryCircleAnalysis__typeLegendIcon"
                  loading="lazy"
                />
                <span class="InventoryCircleAnalysis__typeLegendStateLabel">{{ entry.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import maplibregl from 'maplibre-gl'
import type { MapGeoJSONFeature } from 'maplibre-gl'
import { select, type BaseType, type Selection, type ValueFn } from 'd3-selection'
import { transition } from 'd3-transition'
import { scaleLinear } from 'd3-scale'
import { max } from 'd3-array'
import { format as d3Format } from 'd3-format'
import { circle as turfCircle, booleanPointInPolygon } from '@turf/turf'
import type { Feature, FeatureCollection, Point, Polygon } from 'geojson'
import { CompetenceTypeOuvrageService } from '@/services/competenceTypeOuvrage/CompetenceTypeOuvrageService'
import { InfrastructureService } from '@/services/infrastructure/InfrastructureService'
import { MarkerGeneratorService } from '@/services/map/MarkerGeneratorService'
import { useMyMapStore } from '@/stores/myMapStore'
import { martinTilesBaseUrl } from '@/config/env'
import type { TypeOuvrageMarkerResult } from '@/services/map/TypeOuvrageMarkerService'
import {
  getResolvedTypeOuvrageVisual,
  prefetchTypeOuvrageVisuals,
} from '@/services/map/TypeOuvrageMarkerService'

const props = defineProps<{
  indicatorKey: string | number
  viewLayerName: string
  typePropertyName: string
  isActive: boolean
  /** Optional competence id for opening infrastructure fiche (indicator 324). */
  competenceId?: number
}>()

const MAP_SOURCE_PREFIX = 'inventory-analysis-source'
const QUERY_LAYER_PREFIX = 'inventory-analysis-query-layer'
const CIRCLE_SOURCE_PREFIX = 'inventory-circle-source'
const CIRCLE_FILL_PREFIX = 'inventory-circle-fill'
const CIRCLE_OUTLINE_PREFIX = 'inventory-circle-outline'
const CIRCLE_MASK_SOURCE_PREFIX = 'inventory-circle-mask-source'
const CIRCLE_MASK_FILL_PREFIX = 'inventory-circle-mask-fill'
const INSIDE_POINTS_SOURCE_PREFIX = 'inventory-circle-inside-source'
const INSIDE_POINTS_LAYER_PREFIX = 'inventory-circle-inside-layer'
const INDICATOR_322 = 322
const INDICATOR_324 = 324
const INDICATOR_328 = 328
const INDICATOR_329 = 329
const MARKER_328_GREEN = 'marker-328-green'
const MARKER_328_RED = 'marker-328-red'
/** Zoom level corresponding to scale 1/4000 (point + picto + ID above this) */
const ZOOM_THRESHOLD_1_4000 = 15
const INVENTORY_PICTO_IMAGE_324 = 'inventory-picto-324'
const CLUSTER_RADIUS = 50
const CLUSTER_MAX_ZOOM = 14
/** Tighter grouping for indicator 329 (durée dernière maintenance) */
const CLUSTER_329_RADIUS = 120
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for future scale logic
const TARGET_SCALE_DENOMINATOR = 188000
const MIN_ZOOM_LEVEL = 10
const EARTH_RADIUS_METERS = 6378137
const SCREEN_DPI = 96
const INCHES_PER_METER = 39.37
const METERS_PER_KILOMETER = 1000
const DEFAULT_RADIUS_METERS = 5000
const MIN_RADIUS_METERS = 500
const MAX_RADIUS_METERS = 20000
const UNKNOWN_TYPE_LABEL = 'Unknown'
const OVERLAY_BAR_MAX_LENGTH = 120
const OVERLAY_BAR_WIDTH = 25
const OVERLAY_START_ANGLE = -90
const OVERLAY_END_ANGLE = 90
const OVERLAY_ANCHOR_COLOR = '#000000'
const OVERLAY_ANCHOR_OPACITY = 0.9
const OVERLAY_VALUE_OFFSET = 10
const OVERLAY_LABEL_LINE_HEIGHT = 12
const OVERLAY_LABEL_VALUE_GAP = -5
const OVERLAY_TRANSITION_MS = 200
const OVERLAY_BAR_COLORS = [
  '#144d7e',
  '#0f766e',
  '#7c3aed',
  '#b45309',
  '#b91c1c',
  '#0369a1',
  '#047857',
  '#fbbf24',
  '#22c55e',
  '#f59e42',
  '#f43f5e',
  '#fde047',
  '#10b981',
  '#6366f1',
  '#e11d48',
  '#c026d3',
  '#fbbf24',
]

/**
 * TYPE_OUVRAGE (water infrastructure) to local icon key for indicator 324 pictograms.
 * SVGs are loaded from @/assets/images/icons/map/ via indicator324PictoIcons.
 */
import {
  DEFAULT_INDICATOR_324_ICON_KEY,
  getIndicator324IconKeysForIteration,
  getSvgForIndicator324IconKey,
} from '@/assets/images/icons/map/indicator324PictoIcons'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const TYPE_OUVRAGE_TO_ICON_KEY: Record<string, string> = {
  PUITS: 'well',
  BORNE_FONTAINE: 'water-tap',
  POINT_EAU: 'water-tap',
  POINT_EAU_POTABLE: 'water-tap',
  SOURCE: 'water-well',
  SOURCE_AMENAGEE: 'water-well',
  USINE_EAU: 'factory',
  RESERVOIR_COUVERT: 'reservoir-point',
  CHATEAU_EAU: 'farm-water-tower',
  FORAGE_CHATEAU: 'farm-water-tower',
  STATION_EPURATION: 'filter',
  FONTAINE: 'fountain',
  FORAGE: 'well-forage',
}

const COMPLETION_322_TYPE_LEGEND_ORDER = [
  'PUITS',
  'FORAGE',
  'BORNE_FONTAINE',
  'POINT_EAU',
  'POINT_EAU_POTABLE',
  'SOURCE',
  'SOURCE_AMENAGEE',
  'USINE_EAU',
  'RESERVOIR_COUVERT',
  'CHATEAU_EAU',
  'FORAGE_CHATEAU',
  'STATION_EPURATION',
  'FONTAINE',
] as const

const MAINTENANCE_329_TYPE_LEGEND_ORDER = COMPLETION_322_TYPE_LEGEND_ORDER

const TYPE_OUVRAGE_LABELS: Record<string, string> = {
  PUITS: 'Puits',
  FORAGE: 'Forage',
  BORNE_FONTAINE: 'Borne fontaine',
  POINT_EAU: "Point d'eau",
  POINT_EAU_POTABLE: "Point d'eau potable",
  SOURCE: 'Source',
  SOURCE_AMENAGEE: 'Source aménagée',
  USINE_EAU: "Usine d'eau",
  RESERVOIR_COUVERT: 'Réservoir couvert',
  CHATEAU_EAU: "Château d'eau",
  FORAGE_CHATEAU: 'Forage château',
  STATION_EPURATION: "Station d'épuration",
  FONTAINE: 'Fontaine',
}

const TYPE_OUVRAGE_ALIASES: Record<string, string> = {
  CHATEAU_D_EAU: 'CHATEAU_EAU',
  SOURCE_AMENAGE: 'SOURCE_AMENAGEE',
  POINT_D_EAU: 'POINT_EAU',
}

function getPictoImageName324(typeOuvrage: string): string {
  if (
    !typeOuvrage ||
    typeOuvrage === UNKNOWN_TYPE_LABEL ||
    !(typeOuvrage in TYPE_OUVRAGE_TO_ICON_KEY)
  ) {
    return INVENTORY_PICTO_IMAGE_324
  }
  return `inventory-picto-324-${typeOuvrage}`
}

function getSvgForTypeOuvrage(typeOuvrage: string): string {
  const iconKey = TYPE_OUVRAGE_TO_ICON_KEY[typeOuvrage] ?? DEFAULT_INDICATOR_324_ICON_KEY
  return getSvgForIndicator324IconKey(iconKey)
}

function normalizeTypeOuvrageKey(rawValue: unknown): string {
  if (rawValue === null || rawValue === undefined) return UNKNOWN_TYPE_LABEL
  const source = String(rawValue).trim()
  if (!source) return UNKNOWN_TYPE_LABEL
  const normalized = source
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  const aliased = TYPE_OUVRAGE_ALIASES[normalized] ?? normalized
  return aliased in TYPE_OUVRAGE_TO_ICON_KEY ? aliased : UNKNOWN_TYPE_LABEL
}

/** Fixed order of types so bar and marker colors match. */
const TYPE_OUVRAGE_ORDER = [...Object.keys(TYPE_OUVRAGE_TO_ICON_KEY)].sort()

/** Types used for donut segments (known types + unknown). */
const DONUT_SEGMENT_TYPES = [...TYPE_OUVRAGE_ORDER, UNKNOWN_TYPE_LABEL]

const COMPLETION_322_BUCKETS = [
  '<100%',
  '100%',
] as const

const MAINTENANCE_329_BUCKETS = ['N/A', '< 1 an', '< 2 ans', '< 5 ans', '>= 5 ans'] as const
type Maintenance329Bucket = (typeof MAINTENANCE_329_BUCKETS)[number]

const selectedMaintenance329Buckets = ref<Record<Maintenance329Bucket, boolean>>({
  'N/A': true,
  '< 1 an': true,
  '< 2 ans': true,
  '< 5 ans': true,
  '>= 5 ans': true,
})

const allMaintenance329Selected = computed({
  get: () => MAINTENANCE_329_BUCKETS.every((bucket) => selectedMaintenance329Buckets.value[bucket]),
  set: (checked: boolean) => {
    selectedMaintenance329Buckets.value = Object.fromEntries(
      MAINTENANCE_329_BUCKETS.map((bucket) => [bucket, checked]),
    ) as Record<Maintenance329Bucket, boolean>
  },
})

function isMaintenance329Bucket(label: string): label is Maintenance329Bucket {
  return (MAINTENANCE_329_BUCKETS as readonly string[]).includes(label)
}

const COMPLETION_322_COLORS: Record<(typeof COMPLETION_322_BUCKETS)[number], string> = {
  '<100%': '#ef4444',
  '100%': '#22c55e',
}

const COMPLETION_322_VALUE_KEYS = ['lt100', '100'] as const

function getCompletionBucketFromValue322(rawValue: unknown): (typeof COMPLETION_322_BUCKETS)[number] {
  if (rawValue === null || rawValue === undefined) {
    return '<100%'
  }
  const numericValue =
    typeof rawValue === 'number'
      ? rawValue
      : Number(String(rawValue).replace(',', '.').replace('%', '').trim())
  if (!Number.isFinite(numericValue)) {
    return '<100%'
  }
  if (numericValue >= 100) return '100%'
  return '<100%'
}

function getCompletionBucketValueKey322(bucket: string): (typeof COMPLETION_322_VALUE_KEYS)[number] {
  return bucket === '100%' ? '100' : 'lt100'
}

/** Build clusterProperties for 324: one count per type so donut charts can show segment sizes. */
function buildClusterProperties324(): Record<string, unknown> {
  const entries: [string, unknown][] = DONUT_SEGMENT_TYPES.map((type) => [
    `count_${type}`,
    ['+', ['case', ['==', ['get', 'typeValue'], type], 1, 0]] as unknown,
  ])
  return Object.fromEntries(entries)
}

function getDonutSegmentTypes(): readonly string[] {
  return isIndicator322.value ? TYPE_OUVRAGE_ORDER : DONUT_SEGMENT_TYPES
}

function buildClusterPropertiesForDonut(): Record<string, unknown> {
  const entries: [string, unknown][] = getDonutSegmentTypes().map((type) => [
    `count_${type}`,
    ['+', ['case', ['==', ['get', 'typeValue'], type], 1, 0]] as unknown,
  ])
  return Object.fromEntries(entries)
}

/**
 * Color for a type d'ouvrage's marker/legend/donut-segment: the admin-configured marker_config
 * color when available (same source as the map pins and the right panel), falling back to the
 * fixed palette-cycling scheme below only until that's prefetched or when the type has none.
 * `typeOuvrageVisualsVersion.value` is read (unused numerically) so callers inside a Vue reactive
 * effect (computed/template) re-evaluate once prefetchTypeOuvrageVisuals() resolves.
 */
function getColorForTypeOuvrage(typeOuvrage: string): string {
  void typeOuvrageVisualsVersion.value
  const resolved = getResolvedTypeOuvrageVisual(typeOuvrage)
  if (resolved) return resolved.color

  const index = TYPE_OUVRAGE_ORDER.indexOf(typeOuvrage)
  const colorIndex = index >= 0 ? index : 0
  return OVERLAY_BAR_COLORS[colorIndex % OVERLAY_BAR_COLORS.length]
}

/**
 * Fully rendered marker SVG for a type d'ouvrage: the admin-configured icon+color when available
 * (see getColorForTypeOuvrage), falling back to the local pictogram catalog otherwise. Drop-in
 * replacement for `MarkerGeneratorService.createMarkerSvgWithIcon(getSvgForTypeOuvrage(x), getColorForTypeOuvrage(x))`
 * wherever no separate status badge is composited on top (legend/gauge icons, 324 map pictos).
 */
function getMarkerSvgForTypeOuvrage(typeOuvrage: string): string {
  void typeOuvrageVisualsVersion.value
  const resolved = getResolvedTypeOuvrageVisual(typeOuvrage)
  if (resolved) return resolved.markerSvg
  return MarkerGeneratorService.createMarkerSvgWithIcon(
    getSvgForTypeOuvrage(typeOuvrage),
    getColorForTypeOuvrage(typeOuvrage),
  )
}

function getColorForLegendLabel(label: string): string {
  if (isIndicator322.value) {
    return getColorForTypeOuvrage(label)
  }
  if (isIndicator329.value) {
    if (label === 'N/A') return MARKER_329_NA_COLOR
    if (label === '< 1 an') return MARKER_329_LT_1_COLOR
    if (label === '< 2 ans') return MARKER_329_LT_2_COLOR
    if (label === '< 5 ans') return MARKER_329_LT_5_COLOR
    if (label === '>= 5 ans') return MARKER_329_GE_5_COLOR
    if (label in TYPE_OUVRAGE_TO_ICON_KEY) return getColorForTypeOuvrage(label)
  }
  return getColorForTypeOuvrage(label)
}

const completion322StatusLegendEntries = computed(() => {
  if (!isIndicator322.value) return []
  return [
    { label: '< 100%', color: COMPLETION_322_COLORS['<100%'] },
    { label: '100%', color: COMPLETION_322_COLORS['100%'] },
  ]
})

const typeOuvrageGaugeIconSrcByType = computed<Record<string, string>>(() => {
  if (!isIndicator322.value) return {}
  return Object.fromEntries(
    TYPE_OUVRAGE_ORDER.map((typeCode) => [
      typeCode,
      `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(getMarkerSvgForTypeOuvrage(typeCode))}`,
    ]),
  )
})

const maintenance329TypeLegendEntries = computed(() => {
  if (!isIndicator329.value) return []
  return MAINTENANCE_329_TYPE_LEGEND_ORDER.map((typeCode) => ({
    label: TYPE_OUVRAGE_LABELS[typeCode] ?? typeCode,
    iconSrc: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(getMarkerSvgForTypeOuvrage(typeCode))}`,
  }))
})

// color is passed by caller for API consistency; path fill is set in HTML at call site
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function donutSegment324(start: number, end: number, r: number, r0: number, color: string): string {
  if (end - start >= 1) end = start + 1 - 0.00001
  const a0 = 2 * Math.PI * (start - 0.25)
  const a1 = 2 * Math.PI * (end - 0.25)
  const x0 = Math.cos(a0)
  const y0 = Math.sin(a0)
  const x1 = Math.cos(a1)
  const y1 = Math.sin(a1)
  const largeArc = end - start > 0.5 ? 1 : 0
  return `M ${r + r0 * x0} ${r + r0 * y0} L ${r + r * x0} ${r + r * y0} A ${r} ${r} 0 ${largeArc} 1 ${r + r * x1} ${r + r * y1} L ${r + r0 * x1} ${r + r0 * y1} A ${r0} ${r0} 0 ${largeArc} 0 ${r + r0 * x0} ${r + r0 * y0}`
}

function createDonutChartElement324(properties: Record<string, number>): HTMLDivElement {
  const total = properties.point_count ?? 0
  const segmentTypes = getDonutSegmentTypes()
  const counts = segmentTypes.map((type) => properties[`count_${type}`] ?? 0)
  let offset = 0
  const offsets = counts.map((c) => {
    const o = offset
    offset += c
    return o / (total || 1)
  })
  const ends = counts.map((c, i) => (offsets[i] + c / (total || 1)) as number)
  const fontSize = total >= 1000 ? 22 : total >= 100 ? 20 : total >= 10 ? 18 : 16
  const r = total >= 1000 ? 50 : total >= 100 ? 32 : total >= 10 ? 24 : 18
  const r0 = Math.round(r * 0.6)
  const w = r * 2
  const isIndicator322Donut = isIndicator322.value
  const centerFillColor = isIndicator322Donut ? '#0f172a' : '#ffffff'
  const centerStrokeColor = isIndicator322Donut ? '#0f172a' : '#ffffff'
  const centerLabelColor = isIndicator322Donut ? '#ffffff' : '#111827'

  let pathHtml = ''
  for (let i = 0; i < counts.length; i++) {
    if (counts[i] <= 0) continue
    pathHtml += `<path d="${donutSegment324(offsets[i], ends[i], r, r0, getColorForLegendLabel(segmentTypes[i]))}" fill="${getColorForLegendLabel(segmentTypes[i])}" />`
  }

  const totalLabel = total >= 1000 ? `${(total / 1000).toFixed(1)}k` : String(total)
  const html = `<div style="cursor: pointer;">
    <svg width="${w}" height="${w}" viewBox="0 0 ${w} ${w}" text-anchor="middle" style="font: ${fontSize}px sans-serif; display: block;">
      ${pathHtml}
      <circle cx="${r}" cy="${r}" r="${r0}" fill="${centerFillColor}" stroke="${centerStrokeColor}" stroke-width="2"/>
      <text dominant-baseline="central" transform="translate(${r}, ${r})" style="font-weight: 600; fill: ${centerLabelColor};">${totalLabel}</text>
    </svg>
  </div>`

  const el = document.createElement('div')
  el.innerHTML = html
  return el.firstChild as HTMLDivElement
}

const mapStore = useMyMapStore()
const radiusMetersValue = ref(DEFAULT_RADIUS_METERS)
const aggregationMap = ref<Record<string, number>>({})
const insidePoints = ref<FeatureCollection<Point>>({
  type: 'FeatureCollection',
  features: [],
})
const circleCenter = ref<maplibregl.LngLat | null>(null)
const circlePolygon = ref<Feature<Polygon> | null>(null)
const dragMarker = ref<unknown | null>(null)
const dragAnimationFrame = ref<number | null>(null)
const isDraggingCircle = ref(false)
const isResizingCircle = ref(false)
const currentScaleDenominator = ref<number | null>(null)
const currentZoomLevel = ref<number>(0)
const isHoveringAnchor = ref(false)
let ensurePictoImages324InFlight: Promise<void> | null = null
let mapIdleHandler: (() => void) | null = null
let mapMouseMoveHandler: ((event: maplibregl.MapMouseEvent) => void) | null = null
let mapMouseUpHandler: ((event: maplibregl.MapMouseEvent) => void) | null = null
type MapClickEvent = maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }
let clickHandler324: ((e: MapClickEvent) => void) | null = null
let clickHandler328: ((e: MapClickEvent) => void) | null = null
let clickHandler329: ((e: MapClickEvent) => void) | null = null
let symbolMouseEnterHandler324: (() => void) | null = null
let symbolMouseLeaveHandler324: (() => void) | null = null
const clusterDonutMarkers324: Record<number, maplibregl.Marker> = {}
const clusterDonutMarkersOnScreen324: Record<number, maplibregl.Marker> = {}
let clusterDonutRenderHandler324: (() => void) | null = null
let clusterDonutDataHandler324:
  | ((e: { sourceId?: string; isSourceLoaded?: boolean }) => void)
  | null = null
const clusterSimpleMarkers328: Record<number, maplibregl.Marker> = {}
const clusterSimpleMarkersOnScreen328: Record<number, maplibregl.Marker> = {}
let clusterSimpleRenderHandler328: (() => void) | null = null
let clusterSimpleDataHandler328:
  | ((e: { sourceId?: string; isSourceLoaded?: boolean }) => void)
  | null = null
const clusterSimpleMarkers329: Record<number, maplibregl.Marker> = {}
const clusterSimpleMarkersOnScreen329: Record<number, maplibregl.Marker> = {}
let clusterSimpleRenderHandler329: (() => void) | null = null
let clusterSimpleDataHandler329:
  | ((e: { sourceId?: string; isSourceLoaded?: boolean }) => void)
  | null = null
let mapMoveHandler: (() => void) | null = null
let mapZoomStartHandler: (() => void) | null = null
let mapZoomEndHandler: (() => void) | null = null
let mapResizeMoveHandler: ((event: maplibregl.MapMouseEvent) => void) | null = null
let mapResizeUpHandler: ((event: maplibregl.MapMouseEvent) => void) | null = null
let mapAnchorHoverHandler: ((event: maplibregl.MapMouseEvent) => void) | null = null
let overlayContainer: HTMLDivElement | null = null
let overlaySvg: SVGSVGElement | null = null
let overlayAnimationFrame: number | null = null

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for radius display
const radiusLabel = computed(() => {
  if (radiusMetersValue.value >= METERS_PER_KILOMETER) {
    const radiusKilometers = radiusMetersValue.value / METERS_PER_KILOMETER
    const roundedKilometers = Number.isInteger(radiusKilometers)
      ? radiusKilometers
      : radiusKilometers.toFixed(1)
    return `${roundedKilometers} km`
  }
  return `${radiusMetersValue.value} m`
})

interface AggregationEntry {
  typeLabel: string
  count: number
}

const aggregationEntries = computed<AggregationEntry[]>(() => {
  const entries = Object.entries(aggregationMap.value).map(([typeLabel, count]) => ({
    typeLabel,
    count,
  }))
  if (isIndicator329.value) {
    const orderMap = new Map<string, number>(
      MAINTENANCE_329_BUCKETS.map((label, index) => [label, index]),
    )
    return entries.sort(
      (a, b) =>
        (orderMap.get(a.typeLabel) ?? Number.MAX_SAFE_INTEGER) -
          (orderMap.get(b.typeLabel) ?? Number.MAX_SAFE_INTEGER) ||
        b.count - a.count,
    )
  }
  return entries.sort((a, b) => b.count - a.count || a.typeLabel.localeCompare(b.typeLabel))
})

const maxCount = computed(() => {
  if (aggregationEntries.value.length === 0) {
    return 1
  }
  return Math.max(...aggregationEntries.value.map((entry) => entry.count))
})

function formatNumber(value: number): string {
  return new Intl.NumberFormat('fr-FR').format(value)
}

/** Indicators 322, 324, 328 and 329: visible from zoom 0 (clusters only). Others: from MIN_ZOOM_LEVEL. */
const minZoomForIndicator = computed(() =>
  isIndicator322.value || isIndicator324.value || isIndicator328.value || isIndicator329.value
    ? 0
    : MIN_ZOOM_LEVEL,
)

const isZoomAllowed = computed(() => {
  return currentZoomLevel.value >= minZoomForIndicator.value
})

const zoomNoticeMessage = computed(() => {
  if (currentZoomLevel.value < minZoomForIndicator.value) {
    return t('myMap.inventoryCircle.zoomNotice', {
      min: minZoomForIndicator.value,
      actuel: Math.round(currentZoomLevel.value),
    })
  }
  return ''
})

const mapSourceId = computed(() => `${MAP_SOURCE_PREFIX}-${props.indicatorKey}`)
const queryLayerId = computed(() => `${QUERY_LAYER_PREFIX}-${props.indicatorKey}`)
const circleSourceId = computed(() => `${CIRCLE_SOURCE_PREFIX}-${props.indicatorKey}`)
const circleFillId = computed(() => `${CIRCLE_FILL_PREFIX}-${props.indicatorKey}`)
const circleOutlineId = computed(() => `${CIRCLE_OUTLINE_PREFIX}-${props.indicatorKey}`)
const circleMaskSourceId = computed(() => `${CIRCLE_MASK_SOURCE_PREFIX}-${props.indicatorKey}`)
const circleMaskFillId = computed(() => `${CIRCLE_MASK_FILL_PREFIX}-${props.indicatorKey}`)
const insidePointsSourceId = computed(() => `${INSIDE_POINTS_SOURCE_PREFIX}-${props.indicatorKey}`)
const insidePointsLayerId = computed(() => `${INSIDE_POINTS_LAYER_PREFIX}-${props.indicatorKey}`)
const isIndicator322 = computed(() => Number(props.indicatorKey) === INDICATOR_322)
const isIndicator324 = computed(() => Number(props.indicatorKey) === INDICATOR_324)
const isIndicator328 = computed(() => Number(props.indicatorKey) === INDICATOR_328)
const isIndicator329 = computed(() => Number(props.indicatorKey) === INDICATOR_329)
const isTypeOuvrageDonutIndicator = computed(() => isIndicator322.value || isIndicator324.value)
const isTypeOuvrageLayer = computed(() => props.typePropertyName === 'type_ouvrage')
const typeOuvrageMarkerResult = ref<TypeOuvrageMarkerResult | null>(null)
let ensureTypeOuvrageMarkerResultInFlight: Promise<void> | null = null
/**
 * Bumped once prefetchTypeOuvrageVisuals() resolves, so computeds reading getColorForTypeOuvrage /
 * getMarkerSvgForTypeOuvrage (which read from that prefetched cache) re-evaluate and pick up the
 * admin-configured icon/color instead of staying on the first-paint hardcoded fallback.
 */
const typeOuvrageVisualsVersion = ref(0)
const insidePointsClusterQueryLayerId = computed(
  () => `${INSIDE_POINTS_LAYER_PREFIX}-${props.indicatorKey}-cluster-query`,
)
const insidePointsSymbolLayerId = computed(
  () => `${INSIDE_POINTS_LAYER_PREFIX}-${props.indicatorKey}-symbol`,
)
const insidePointsTextLayerId = computed(
  () => `${INSIDE_POINTS_LAYER_PREFIX}-${props.indicatorKey}-text`,
)

function ensureVectorSource() {
  const map = mapStore.myMap?.map
  if (!map || map.getSource(mapSourceId.value)) {
    return
  }

  const martinBaseUrl = martinTilesBaseUrl
  if (!martinBaseUrl) {
    console.error('[InventoryCircleAnalysis] Martin tiles base URL is not defined (VITE_MARTIN_TILES_URL or VITE_API_URL + /martin)')
    return
  }

  const cleanBaseUrl = martinBaseUrl.replace(/(https?:\/)\/{2,}/g, '$1/').replace(/\/+$/, '')
  const cleanLayerName = props.viewLayerName.replace(/^\/+/, '')
  const tilesUrl = `${cleanBaseUrl}/${cleanLayerName}/{z}/{x}/{y}`
  mapStore.myMap?.addVectorTileSource(mapSourceId.value, tilesUrl, 0, 22)
}

function updateScaleDenominator() {
  const map = mapStore.myMap?.map
  if (!map) {
    currentScaleDenominator.value = null
    currentZoomLevel.value = 0
    return
  }

  const zoom = map.getZoom()
  currentZoomLevel.value = zoom
  const latitude = map.getCenter().lat
  const tileSize = ((map as unknown as { transform?: { tileSize?: number } })?.transform
    ?.tileSize ?? 512) as number
  const metersPerPixel =
    (Math.cos((latitude * Math.PI) / 180) * 2 * Math.PI * EARTH_RADIUS_METERS) /
    (tileSize * Math.pow(2, zoom))
  const scaleDenominator = metersPerPixel * SCREEN_DPI * INCHES_PER_METER
  console.assert(
    scaleDenominator > 0,
    '[InventoryCircleAnalysis] Scale denominator must be positive',
  )
  currentScaleDenominator.value = scaleDenominator
}

function getMetersPerPixel(): number | null {
  if (currentScaleDenominator.value === null) {
    return null
  }
  return currentScaleDenominator.value / (SCREEN_DPI * INCHES_PER_METER)
}

async function ensurePictoImages324(): Promise<void> {
  const map = mapStore.myMap?.map
  if (!map) {
    return
  }
  // Warmed before the `hasImage` short-circuit below (images are registered once and never
  // rebuilt), so the per-type pictos are built with the final admin-configured appearance from
  // their first (and effectively only) registration instead of getting stuck on the fallback.
  await prefetchTypeOuvrageVisuals()
  if (map.hasImage(INVENTORY_PICTO_IMAGE_324)) {
    return
  }
  if (ensurePictoImages324InFlight) {
    await ensurePictoImages324InFlight
    return
  }
  ensurePictoImages324InFlight = (async () => {
    try {
      const mapRef = mapStore.myMap?.map
      if (!mapRef || mapRef.hasImage(INVENTORY_PICTO_IMAGE_324)) {
        return
      }
      const { MarkerGeneratorService } = await import('@/services/map/MarkerGeneratorService')

      const addPictoImageFromMarkerSvg = async (imageName: string, markerSvg: string): Promise<void> => {
        if (mapRef.hasImage(imageName)) {
          return
        }
        try {
          const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
          if (img && !mapRef.hasImage(imageName)) {
            mapRef.addImage(imageName, img)
          }
        } catch (addError) {
          const message = addError instanceof Error ? addError.message : String(addError)
          if (!message.includes('already exists')) {
            throw addError
          }
        }
      }

      const defaultSvg = getSvgForIndicator324IconKey(DEFAULT_INDICATOR_324_ICON_KEY)
      await addPictoImageFromMarkerSvg(
        INVENTORY_PICTO_IMAGE_324,
        MarkerGeneratorService.createMarkerSvgWithIcon(defaultSvg, OVERLAY_BAR_COLORS[0]),
      )
      for (const typeOuvrage of Object.keys(TYPE_OUVRAGE_TO_ICON_KEY)) {
        await addPictoImageFromMarkerSvg(getPictoImageName324(typeOuvrage), getMarkerSvgForTypeOuvrage(typeOuvrage))
      }
    } catch (error) {
      console.error('[InventoryCircleAnalysis] Failed to load picto images for 324:', error)
    } finally {
      ensurePictoImages324InFlight = null
    }
  })()
  await ensurePictoImages324InFlight
}

async function ensureTypeOuvrageMarkerResult324(): Promise<void> {
  if (!isTypeOuvrageLayer.value || !isTypeOuvrageDonutIndicator.value) return
  const map = mapStore.myMap?.map
  if (!map) return
  if (typeOuvrageMarkerResult.value) return
  if (ensureTypeOuvrageMarkerResultInFlight) {
    await ensureTypeOuvrageMarkerResultInFlight
    return
  }
  ensureTypeOuvrageMarkerResultInFlight = (async () => {
    try {
      const { getTypeToMarkerConfigMap, ensureTypeOuvrageImagesOnMap } = await import(
        '@/services/map/TypeOuvrageMarkerService'
      )
      const { MartinStyleService } = await import('@/services/map/MartinStyleService')
      const typeToConfig = await getTypeToMarkerConfigMap()
      const atgId = Number(props.indicatorKey)
      const style = await MartinStyleService.loadStyleFromBackend(atgId)
      let defaultMarker: Partial<{ markerColor: string; lucideIcon: string; iconColor: string; iconSize: number }> | null = null
      if (style?.metadata && typeof style.metadata === 'object') {
        const metadata = style.metadata as Record<string, unknown>
        if (metadata.marker && typeof metadata.marker === 'object') {
          const m = metadata.marker as Record<string, unknown>
          defaultMarker = {
            markerColor: m.markerColor as string,
            lucideIcon: m.lucideIcon as string,
            iconColor: m.iconColor as string,
            iconSize: (m.iconSize as number) ?? 0.6,
          }
        }
      }
      const result = await ensureTypeOuvrageImagesOnMap(
        map,
        props.indicatorKey,
        typeToConfig,
        defaultMarker,
      )
      typeOuvrageMarkerResult.value = result
    } catch (error) {
      console.error('[InventoryCircleAnalysis] Failed to load type_ouvrage marker config:', error)
    } finally {
      ensureTypeOuvrageMarkerResultInFlight = null
    }
  })()
  await ensureTypeOuvrageMarkerResultInFlight
}

const MARKER_328_GREEN_COLOR = '#22c55e'
const MARKER_328_RED_COLOR = '#ef4444'
const CLUSTER_328_ORANGE = '#f97316'
const DATE_CONSTRUCTION_PROPERTY = 'DATE_CONSTRUCTION'
const VALEUR_CALCULEE_PROPERTY = 'valeur_calculee'
const MARKER_329_NA_COLOR = '#6b7280'
const MARKER_329_LT_1_COLOR = '#22c55e'
const MARKER_329_LT_2_COLOR = '#eab308'
const MARKER_329_LT_5_COLOR = '#000000'
const MARKER_329_GE_5_COLOR = '#ef4444'
const CLUSTER_329_ORANGE = '#f97316'

let ensureMarkerImages328InFlight: Promise<void> | null = null

async function ensureMarkerImages328(): Promise<void> {
  const map = mapStore.myMap?.map
  if (!map) return
  if (map.hasImage(MARKER_328_GREEN) && map.hasImage(MARKER_328_RED)) return
  if (ensureMarkerImages328InFlight) {
    await ensureMarkerImages328InFlight
    return
  }
  ensureMarkerImages328InFlight = (async () => {
    const mapRef = mapStore.myMap?.map
    if (!mapRef) return
    try {
      const { MarkerGeneratorService } = await import('@/services/map/MarkerGeneratorService')
      const wellSvg = getSvgForIndicator324IconKey('well')
      if (!mapRef.hasImage(MARKER_328_GREEN)) {
        const greenSvg = MarkerGeneratorService.createMarkerSvgWithIcon(
          wellSvg,
          MARKER_328_GREEN_COLOR,
        )
        const greenImg = await MarkerGeneratorService.createImageFromSvg(greenSvg)
        if (greenImg) mapRef.addImage(MARKER_328_GREEN, greenImg)
      }
      if (!mapRef.hasImage(MARKER_328_RED)) {
        const redSvg = MarkerGeneratorService.createMarkerSvgWithIcon(wellSvg, MARKER_328_RED_COLOR)
        const redImg = await MarkerGeneratorService.createImageFromSvg(redSvg)
        if (redImg) mapRef.addImage(MARKER_328_RED, redImg)
      }
    } catch (error) {
      console.error('[InventoryCircleAnalysis] Failed to load marker images for 328:', error)
    } finally {
      ensureMarkerImages328InFlight = null
    }
  })()
  await ensureMarkerImages328InFlight
}

function isDateFilledFor328(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '' || trimmed.toUpperCase() === 'N/A') return false
  }
  return true
}

const VALUE_329_KEYS = ['na', 'lt1', 'lt2', 'lt5', 'ge5'] as const

function getMaintenanceBucketKey329(bucket: Maintenance329Bucket): (typeof VALUE_329_KEYS)[number] {
  if (bucket === 'N/A') return 'na'
  if (bucket === '< 1 an') return 'lt1'
  if (bucket === '< 2 ans') return 'lt2'
  if (bucket === '< 5 ans') return 'lt5'
  return 'ge5'
}

function classifyMaintenanceAgeYears329(years: number): Maintenance329Bucket {
  if (years < 1) return '< 1 an'
  if (years < 2) return '< 2 ans'
  if (years >= 5) return '>= 5 ans'
  return '< 5 ans'
}

function parseMaintenanceAgeYearsFromDate329(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  const isoDate = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (isoDate) {
    const parsed = new Date(trimmed)
    if (Number.isNaN(parsed.getTime())) return null
    const ageYears = (Date.now() - parsed.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    return Number.isFinite(ageYears) && ageYears >= 0 ? ageYears : null
  }

  const frDate = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (frDate) {
    const [, dd, mm, yyyy] = frDate
    const parsed = new Date(`${yyyy}-${mm}-${dd}`)
    if (Number.isNaN(parsed.getTime())) return null
    const ageYears = (Date.now() - parsed.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    return Number.isFinite(ageYears) && ageYears >= 0 ? ageYears : null
  }

  return null
}

function getMaintenanceBucketFromValue329(rawValue: unknown): Maintenance329Bucket {
  if (rawValue === null || rawValue === undefined) {
    return 'N/A'
  }

  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return classifyMaintenanceAgeYears329(rawValue)
  }

  const source = String(rawValue).trim()
  if (!source) return 'N/A'

  const normalized = source
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(',', '.')
    .trim()

  if (normalized === 'n/a' || normalized === 'na' || normalized === 'nd') {
    return 'N/A'
  }

  const dateAgeYears = parseMaintenanceAgeYearsFromDate329(source)
  if (dateAgeYears !== null) {
    return classifyMaintenanceAgeYears329(dateAgeYears)
  }

  if (/(?:>=|>)\s*5/.test(normalized)) return '>= 5 ans'
  if (/<\s*1/.test(normalized)) return '< 1 an'
  if (/<\s*2/.test(normalized)) return '< 2 ans'
  if (/<\s*5/.test(normalized)) return '< 5 ans'

  const numberMatch = normalized.match(/-?\d+(?:\.\d+)?/)
  if (numberMatch) {
    const numericValue = Number(numberMatch[0])
    if (Number.isFinite(numericValue)) {
      if (numericValue >= 5) return '>= 5 ans'
      return classifyMaintenanceAgeYears329(numericValue)
    }
  }

  return 'N/A'
}

/** Icon per type d'ouvrage (324 pictos), color per valeur_calculee. */
function getMarker329ImageName(typeOuvrage: string, maintenanceBucket: Maintenance329Bucket): string {
  const normalizedType =
    typeOuvrage in TYPE_OUVRAGE_TO_ICON_KEY ? typeOuvrage : UNKNOWN_TYPE_LABEL
  const valueKey = getMaintenanceBucketKey329(maintenanceBucket)
  return `marker-329-badge-${normalizedType}-${valueKey}`
}

const VALUE_329_COLORS: Record<string, string> = {
  na: MARKER_329_NA_COLOR,
  lt1: MARKER_329_LT_1_COLOR,
  lt2: MARKER_329_LT_2_COLOR,
  lt5: MARKER_329_LT_5_COLOR,
  ge5: MARKER_329_GE_5_COLOR,
}

const VALUE_322_COLORS: Record<(typeof COMPLETION_322_VALUE_KEYS)[number], string> = {
  lt100: COMPLETION_322_COLORS['<100%'],
  '100': COMPLETION_322_COLORS['100%'],
}

function getMarker322ImageName(typeOuvrage: string, completionBucket: string): string {
  const valueKey = getCompletionBucketValueKey322(completionBucket)
  return `marker-322-badge-${typeOuvrage}-${valueKey}`
}

let ensureMarkerImages322InFlight: Promise<void> | null = null

async function ensureMarkerImages322(): Promise<void> {
  const map = mapStore.myMap?.map
  if (!map) return
  // Warmed before the `allPresent` short-circuit below (badges are registered once and never
  // rebuilt), so they're built with the final admin-configured pin color from the start.
  await prefetchTypeOuvrageVisuals()
  const typeCodes = Object.keys(TYPE_OUVRAGE_TO_ICON_KEY)
  const allNames = typeCodes.flatMap((typeCode) =>
    COMPLETION_322_VALUE_KEYS.map((valueKey) => `marker-322-badge-${typeCode}-${valueKey}`),
  )
  const allPresent = allNames.every((name) => map.hasImage(name))
  if (allPresent) return
  if (ensureMarkerImages322InFlight) {
    await ensureMarkerImages322InFlight
    return
  }
  ensureMarkerImages322InFlight = (async () => {
    const mapRef = mapStore.myMap?.map
    if (!mapRef) return
    try {
      const { MarkerGeneratorService } = await import('@/services/map/MarkerGeneratorService')
      for (const typeCode of typeCodes) {
        const svg = getSvgForTypeOuvrage(typeCode)
        for (const valueKey of COMPLETION_322_VALUE_KEYS) {
          const imageName = `marker-322-badge-${typeCode}-${valueKey}`
          if (mapRef.hasImage(imageName)) continue
          const color = VALUE_322_COLORS[valueKey] ?? COMPLETION_322_COLORS['<100%']
          const markerSvg = MarkerGeneratorService.createMarkerSvgWithIcon(
            svg,
            getColorForTypeOuvrage(typeCode),
            color,
          )
          const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
          if (img) mapRef.addImage(imageName, img)
        }
      }
    } catch (error) {
      console.error('[InventoryCircleAnalysis] Failed to load marker images for 322:', error)
    } finally {
      ensureMarkerImages322InFlight = null
    }
  })()
  await ensureMarkerImages322InFlight
}

let ensureMarkerImages329InFlight: Promise<void> | null = null

async function ensureMarkerImages329(): Promise<void> {
  const map = mapStore.myMap?.map
  if (!map) return
  // Warmed before the `allPresent` short-circuit below (badges are registered once and never
  // rebuilt), so they're built with the final admin-configured pin color from the start.
  await prefetchTypeOuvrageVisuals()
  const typeCodes = [...Object.keys(TYPE_OUVRAGE_TO_ICON_KEY), UNKNOWN_TYPE_LABEL]
  const valueKeys = [...VALUE_329_KEYS]
  const allNames = typeCodes.flatMap((typeCode) =>
    valueKeys.map((valueKey) => `marker-329-badge-${typeCode}-${valueKey}`),
  )
  const allPresent = allNames.every((name) => map.hasImage(name))
  if (allPresent) return
  if (ensureMarkerImages329InFlight) {
    await ensureMarkerImages329InFlight
    return
  }
  ensureMarkerImages329InFlight = (async () => {
    const mapRef = mapStore.myMap?.map
    if (!mapRef) return
    try {
      const { MarkerGeneratorService } = await import('@/services/map/MarkerGeneratorService')
      for (const typeCode of typeCodes) {
        const svg =
          typeCode === UNKNOWN_TYPE_LABEL
            ? getSvgForIndicator324IconKey(DEFAULT_INDICATOR_324_ICON_KEY)
            : getSvgForTypeOuvrage(typeCode)
        const markerColor =
          typeCode === UNKNOWN_TYPE_LABEL ? OVERLAY_BAR_COLORS[0] : getColorForTypeOuvrage(typeCode)
        for (const valueKey of valueKeys) {
          const imageName = `marker-329-badge-${typeCode}-${valueKey}`
          if (mapRef.hasImage(imageName)) continue
          const badgeColor = VALUE_329_COLORS[valueKey] ?? MARKER_329_NA_COLOR
          const markerSvg = MarkerGeneratorService.createMarkerSvgWithIcon(
            svg,
            markerColor,
            badgeColor,
          )
          const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
          if (img) mapRef.addImage(imageName, img)
        }
      }
    } catch (error) {
      console.error('[InventoryCircleAnalysis] Failed to load marker images for 329:', error)
    } finally {
      ensureMarkerImages329InFlight = null
    }
  })()
  await ensureMarkerImages329InFlight
}

function ensureQueryLayer() {
  const map = mapStore.myMap?.map
  if (!map || map.getLayer(queryLayerId.value)) {
    return
  }

  map.addLayer({
    id: queryLayerId.value,
    type: 'circle',
    source: mapSourceId.value,
    'source-layer': normalizeSourceLayerName(props.viewLayerName),
    paint: {
      'circle-radius': 1,
      'circle-opacity': 0,
    },
    metadata: { isPersistent: false },
  })
}

const titreAttributs = () => t('myMap.inventoryCircle.attributesTitle')

async function handle324SymbolClick(event: MapClickEvent): Promise<void> {
  if (!event.features || event.features.length === 0) {
    return
  }
  const feature = event.features[0]
  const properties = (feature.properties ?? {}) as Record<string, unknown>
  const popupTitle =
    (properties.ift_intitule as string) || (properties.intitule as string) || titreAttributs()
  const tableData: Record<string, unknown> = {}
  Object.keys(properties).forEach((key) => {
    if (key === key.toUpperCase() && key !== key.toLowerCase()) {
      const value = properties[key]
      if (value !== undefined && value !== null) {
        tableData[key] = value
      }
    }
  })
  let popupValue: unknown = Object.keys(tableData).length > 0 ? tableData : null
  if (popupValue === null && typeof properties.attributs === 'string') {
    try {
      const parsed = JSON.parse(properties.attributs as string)
      if (typeof parsed === 'object' && parsed !== null) {
        popupValue = parsed
      }
    } catch {
      // ignore
    }
  }
  const visibleAttributesCount =
    popupValue !== null && typeof popupValue === 'object' && !Array.isArray(popupValue)
      ? Object.keys(popupValue).length
      : 0
  const idInfra = properties.ift_id ?? properties.id ?? properties.gid ?? properties.ogc_fid
  const idInfrastructure =
    idInfra !== undefined && idInfra !== null
      ? typeof idInfra === 'number'
        ? idInfra
        : String(idInfra)
      : undefined

  const typeOuvrageCodeRaw = properties.type_ouvrage ?? properties.TYPE_OUVRAGE
  const typeOuvrageCode = typeOuvrageCodeRaw != null ? String(typeOuvrageCodeRaw).trim() : ''

  let totalAttributesCount = visibleAttributesCount
  if (props.competenceId != null && typeOuvrageCode !== '') {
    try {
      const attributs = await CompetenceTypeOuvrageService.getAttributsForCompetenceAndType(
        props.competenceId,
        typeOuvrageCode,
      )
      totalAttributesCount = attributs.length
    } catch {
      // Fallback: try attribute_values by infrastructure id
      if (idInfrastructure != null) {
        try {
          const attributeValues = await InfrastructureService.getAttributeValues(idInfrastructure)
          if (Array.isArray(attributeValues)) {
            totalAttributesCount = attributeValues.length
          } else if (
            attributeValues != null &&
            typeof attributeValues === 'object' &&
            !Array.isArray(attributeValues)
          ) {
            const hydraMember = (attributeValues as Record<string, unknown>)['hydra:member']
            totalAttributesCount = Array.isArray(hydraMember)
              ? hydraMember.length
              : Object.keys(attributeValues).length
          }
        } catch {
          // keep totalAttributesCount = visibleAttributesCount
        }
      }
    }
  } else if (idInfrastructure != null) {
    try {
      const attributeValues = await InfrastructureService.getAttributeValues(idInfrastructure)
      if (Array.isArray(attributeValues)) {
        totalAttributesCount = attributeValues.length
      } else if (
        attributeValues != null &&
        typeof attributeValues === 'object' &&
        !Array.isArray(attributeValues)
      ) {
        const hydraMember = (attributeValues as Record<string, unknown>)['hydra:member']
        totalAttributesCount = Array.isArray(hydraMember)
          ? hydraMember.length
          : Object.keys(attributeValues).length
      }
    } catch {
      // keep totalAttributesCount = visibleAttributesCount
    }
  }

  const IndicatorPopup = (await import('@/views/map/components/IndicatorPopup.vue')).default
  const { createApp, h } = await import('vue')
  const { i18nInstance } = await import('@/plugins/i18n')

  const popupComponent = h(IndicatorPopup, {
    title: popupTitle || undefined,
    value: popupValue as unknown as string | number | null | undefined,
    format: 'table',
    idInfrastructure,
    showFicheAttributsButton: idInfrastructure != null,
    visibleAttributesCount,
    totalAttributesCount,
    onOpenFiche:
      idInfrastructure != null
        ? (id: string | number) =>
            mapStore.openInfrastructureFicheModal(
              id,
              popupTitle ?? undefined,
              props.competenceId,
              properties,
            )
        : undefined,
  })

  const app = createApp({ render: () => popupComponent })
  app.use(i18nInstance)
  const container = document.createElement('div')
  app.mount(container)

  if (mapStore.myMap?.addPopup) {
    mapStore.myMap.addPopup(event.lngLat, container, false)
  }
}

const POPUP_TITLE_328 = 'Date de construction'

async function handle328SymbolClick(event: MapClickEvent): Promise<void> {
  if (!event.features || event.features.length === 0) {
    return
  }
  const feature = event.features[0]
  const properties = (feature.properties ?? {}) as Record<string, unknown>
  const popupValue = properties[DATE_CONSTRUCTION_PROPERTY] ?? null
  const popupTitle =
    (properties.ift_intitule as string) || (properties.intitule as string) || POPUP_TITLE_328
  const idInfra = properties.ift_id ?? properties.id ?? properties.gid ?? properties.ogc_fid
  const idInfrastructure =
    idInfra !== undefined && idInfra !== null
      ? typeof idInfra === 'number'
        ? idInfra
        : String(idInfra)
      : undefined
  const IndicatorPopup = (await import('@/views/map/components/IndicatorPopup.vue')).default
  const { createApp, h } = await import('vue')
  const { i18nInstance } = await import('@/plugins/i18n')
  const popupComponent = h(IndicatorPopup, {
    title: POPUP_TITLE_328,
    value: popupValue as string | number | null | undefined,
    format: 'text',
    idInfrastructure,
    showFicheAttributsButton: idInfrastructure != null,
    onOpenFiche:
      idInfrastructure != null
        ? (id: string | number) =>
            mapStore.openInfrastructureFicheModal(
              id,
              popupTitle ?? undefined,
              props.competenceId,
              properties,
            )
        : undefined,
  })
  const app = createApp({ render: () => popupComponent })
  app.use(i18nInstance)
  const container = document.createElement('div')
  app.mount(container)
  if (mapStore.myMap?.addPopup) {
    mapStore.myMap.addPopup(event.lngLat, container, false)
  }
}

const titreMaintenance = () => t('myMap.inventoryCircle.lastMaintenanceTitle')

async function handle329SymbolClick(event: MapClickEvent): Promise<void> {
  if (!event.features || event.features.length === 0) {
    return
  }
  const feature = event.features[0]
  const properties = (feature.properties ?? {}) as Record<string, unknown>
  const popupValue = properties[VALEUR_CALCULEE_PROPERTY] ?? null
  const popupTitle =
    (properties.ift_intitule as string) || (properties.intitule as string) || titreMaintenance()
  const idInfra = properties.ift_id ?? properties.id ?? properties.gid ?? properties.ogc_fid
  const idInfrastructure =
    idInfra !== undefined && idInfra !== null
      ? typeof idInfra === 'number'
        ? idInfra
        : String(idInfra)
      : undefined
  const IndicatorPopup = (await import('@/views/map/components/IndicatorPopup.vue')).default
  const { createApp, h } = await import('vue')
  const { i18nInstance } = await import('@/plugins/i18n')
  const popupComponent = h(IndicatorPopup, {
    title: titreMaintenance(),
    value: popupValue as string | number | null | undefined,
    format: 'text',
    idInfrastructure,
    showFicheAttributsButton: idInfrastructure != null,
    onOpenFiche:
      idInfrastructure != null
        ? (id: string | number) =>
            mapStore.openInfrastructureFicheModal(
              id,
              popupTitle ?? undefined,
              props.competenceId,
              properties,
            )
        : undefined,
  })
  const app = createApp({ render: () => popupComponent })
  app.use(i18nInstance)
  const container = document.createElement('div')
  app.mount(container)
  if (mapStore.myMap?.addPopup) {
    mapStore.myMap.addPopup(event.lngLat, container, false)
  }
}

function updateClusterDonutMarkers324(): void {
  const map = mapStore.myMap?.map
  if (!map || !map.getLayer(insidePointsClusterQueryLayerId.value)) {
    return
  }
  if (map.getZoom() >= ZOOM_THRESHOLD_1_4000) {
    for (const id of Object.keys(clusterDonutMarkersOnScreen324)) {
      clusterDonutMarkersOnScreen324[Number(id)].remove()
      delete clusterDonutMarkersOnScreen324[Number(id)]
    }
    return
  }
  const bounds = map.getBounds()
  const sw = map.project(bounds.getSouthWest())
  const ne = map.project(bounds.getNorthEast())
  let features: MapGeoJSONFeature[]
  try {
    features = map.queryRenderedFeatures([sw, ne], {
      layers: [insidePointsClusterQueryLayerId.value],
    })
  } catch {
    return
  }
  const newMarkersOnScreen: Record<number, maplibregl.Marker> = {}
  for (const feature of features) {
    const props = feature.properties as Record<string, number> | undefined
    if (!props || typeof props.point_count !== 'number') continue
    const clusterId = props.cluster_id as number
    const coords = (feature.geometry as Point).coordinates as [number, number]

    let marker = clusterDonutMarkers324[clusterId]
    if (!marker) {
      const el = createDonutChartElement324(props)
      marker = clusterDonutMarkers324[clusterId] = new maplibregl.Marker({ element: el }).setLngLat(
        [coords[0], coords[1]],
      )
    }
    newMarkersOnScreen[clusterId] = marker
    if (!clusterDonutMarkersOnScreen324[clusterId]) {
      marker.addTo(map)
    }
  }
  for (const id of Object.keys(clusterDonutMarkersOnScreen324)) {
    if (!newMarkersOnScreen[Number(id)]) {
      clusterDonutMarkersOnScreen324[Number(id)].remove()
      delete clusterDonutMarkersOnScreen324[Number(id)]
    }
  }
  Object.assign(clusterDonutMarkersOnScreen324, newMarkersOnScreen)
}

function ensureClusterDonutMarkers324(): void {
  const map = mapStore.myMap?.map
  if (!map || clusterDonutRenderHandler324) {
    return
  }
  clusterDonutRenderHandler324 = () => {
    if (!map.getSource(insidePointsSourceId.value)) return
    updateClusterDonutMarkers324()
  }
  map.on('render', clusterDonutRenderHandler324)
  clusterDonutDataHandler324 = (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
    if (e.sourceId === insidePointsSourceId.value && e.isSourceLoaded) {
      updateClusterDonutMarkers324()
    }
  }
  map.on('data', clusterDonutDataHandler324)
  updateClusterDonutMarkers324()
}

function createSimpleClusterMarker328(properties: Record<string, number>): HTMLDivElement {
  const total = properties.point_count ?? 0
  const fontSize = total >= 1000 ? 22 : total >= 100 ? 20 : total >= 10 ? 18 : 16
  const r = total >= 1000 ? 50 : total >= 100 ? 32 : total >= 10 ? 24 : 18
  const w = r * 2
  const totalLabel = total >= 1000 ? `${(total / 1000).toFixed(1)}k` : String(total)
  const html = `<div style="cursor: pointer;">
    <svg width="${w}" height="${w}" viewBox="0 0 ${w} ${w}" text-anchor="middle" style="display: block;">
      <circle cx="${r}" cy="${r}" r="${r}" fill="${CLUSTER_328_ORANGE}" stroke="#fff" stroke-width="2"/>
      <text x="${r}" y="${r}" dominant-baseline="central" text-anchor="middle" style="font: ${fontSize}px sans-serif; font-weight: 600; fill: white;">${totalLabel}</text>
    </svg>
  </div>`
  const el = document.createElement('div')
  el.innerHTML = html
  return el.firstChild as HTMLDivElement
}

function updateClusterSimpleMarkers328(): void {
  const map = mapStore.myMap?.map
  if (!map || !map.getLayer(insidePointsClusterQueryLayerId.value)) {
    return
  }
  if (map.getZoom() >= ZOOM_THRESHOLD_1_4000) {
    for (const id of Object.keys(clusterSimpleMarkersOnScreen328)) {
      clusterSimpleMarkersOnScreen328[Number(id)].remove()
      delete clusterSimpleMarkersOnScreen328[Number(id)]
    }
    return
  }
  const bounds = map.getBounds()
  const sw = map.project(bounds.getSouthWest())
  const ne = map.project(bounds.getNorthEast())
  let features: MapGeoJSONFeature[]
  try {
    features = map.queryRenderedFeatures([sw, ne], {
      layers: [insidePointsClusterQueryLayerId.value],
    })
  } catch {
    return
  }
  const newMarkersOnScreen: Record<number, maplibregl.Marker> = {}
  for (const feature of features) {
    const props = feature.properties as Record<string, number> | undefined
    if (!props || typeof props.point_count !== 'number') continue
    const clusterId = props.cluster_id as number
    const coords = (feature.geometry as Point).coordinates as [number, number]

    let marker = clusterSimpleMarkers328[clusterId]
    if (!marker) {
      const el = createSimpleClusterMarker328(props)
      marker = clusterSimpleMarkers328[clusterId] = new maplibregl.Marker({
        element: el,
      }).setLngLat([coords[0], coords[1]])
    }
    newMarkersOnScreen[clusterId] = marker
    if (!clusterSimpleMarkersOnScreen328[clusterId]) {
      marker.addTo(map)
    }
  }
  for (const id of Object.keys(clusterSimpleMarkersOnScreen328)) {
    if (!newMarkersOnScreen[Number(id)]) {
      clusterSimpleMarkersOnScreen328[Number(id)].remove()
      delete clusterSimpleMarkersOnScreen328[Number(id)]
    }
  }
  Object.assign(clusterSimpleMarkersOnScreen328, newMarkersOnScreen)
}

function ensureClusterSimpleMarkers328(): void {
  const map = mapStore.myMap?.map
  if (!map || clusterSimpleRenderHandler328) {
    return
  }
  clusterSimpleRenderHandler328 = () => {
    if (!map.getSource(insidePointsSourceId.value)) return
    updateClusterSimpleMarkers328()
  }
  map.on('render', clusterSimpleRenderHandler328)
  clusterSimpleDataHandler328 = (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
    if (e.sourceId === insidePointsSourceId.value && e.isSourceLoaded) {
      updateClusterSimpleMarkers328()
    }
  }
  map.on('data', clusterSimpleDataHandler328)
  updateClusterSimpleMarkers328()
}

function createSimpleClusterMarker329(properties: Record<string, number>): HTMLDivElement {
  const total = properties.point_count ?? 0
  const fontSize = total >= 1000 ? 22 : total >= 100 ? 20 : total >= 10 ? 18 : 16
  const r = total >= 1000 ? 50 : total >= 100 ? 32 : total >= 10 ? 24 : 18
  const w = r * 2
  const totalLabel = total >= 1000 ? `${(total / 1000).toFixed(1)}k` : String(total)
  const html = `<div style="cursor: pointer;">
    <svg width="${w}" height="${w}" viewBox="0 0 ${w} ${w}" text-anchor="middle" style="display: block;">
      <circle cx="${r}" cy="${r}" r="${r}" fill="${CLUSTER_329_ORANGE}" stroke="#fff" stroke-width="2"/>
      <text x="${r}" y="${r}" dominant-baseline="central" text-anchor="middle" style="font: ${fontSize}px sans-serif; font-weight: 600; fill: white;">${totalLabel}</text>
    </svg>
  </div>`
  const el = document.createElement('div')
  el.innerHTML = html
  return el.firstChild as HTMLDivElement
}

function updateClusterSimpleMarkers329(): void {
  const map = mapStore.myMap?.map
  if (!map || !map.getLayer(insidePointsClusterQueryLayerId.value)) {
    return
  }
  if (map.getZoom() >= ZOOM_THRESHOLD_1_4000) {
    for (const id of Object.keys(clusterSimpleMarkersOnScreen329)) {
      clusterSimpleMarkersOnScreen329[Number(id)].remove()
      delete clusterSimpleMarkersOnScreen329[Number(id)]
    }
    return
  }
  const bounds = map.getBounds()
  const sw = map.project(bounds.getSouthWest())
  const ne = map.project(bounds.getNorthEast())
  let features: MapGeoJSONFeature[]
  try {
    features = map.queryRenderedFeatures([sw, ne], {
      layers: [insidePointsClusterQueryLayerId.value],
    })
  } catch {
    return
  }
  const newMarkersOnScreen: Record<number, maplibregl.Marker> = {}
  for (const feature of features) {
    const props = feature.properties as Record<string, number> | undefined
    if (!props || typeof props.point_count !== 'number') continue
    const clusterId = props.cluster_id as number
    const coords = (feature.geometry as Point).coordinates as [number, number]

    let marker = clusterSimpleMarkers329[clusterId]
    if (!marker) {
      const el = createSimpleClusterMarker329(props)
      marker = clusterSimpleMarkers329[clusterId] = new maplibregl.Marker({
        element: el,
      }).setLngLat([coords[0], coords[1]])
    }
    newMarkersOnScreen[clusterId] = marker
    if (!clusterSimpleMarkersOnScreen329[clusterId]) {
      marker.addTo(map)
    }
  }
  for (const id of Object.keys(clusterSimpleMarkersOnScreen329)) {
    if (!newMarkersOnScreen[Number(id)]) {
      clusterSimpleMarkersOnScreen329[Number(id)].remove()
      delete clusterSimpleMarkersOnScreen329[Number(id)]
    }
  }
  Object.assign(clusterSimpleMarkersOnScreen329, newMarkersOnScreen)
}

function ensureClusterSimpleMarkers329(): void {
  const map = mapStore.myMap?.map
  if (!map || clusterSimpleRenderHandler329) {
    return
  }
  clusterSimpleRenderHandler329 = () => {
    if (!map.getSource(insidePointsSourceId.value)) return
    updateClusterSimpleMarkers329()
  }
  map.on('render', clusterSimpleRenderHandler329)
  clusterSimpleDataHandler329 = (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
    if (e.sourceId === insidePointsSourceId.value && e.isSourceLoaded) {
      updateClusterSimpleMarkers329()
    }
  }
  map.on('data', clusterSimpleDataHandler329)
  updateClusterSimpleMarkers329()
}

function ensureInsidePointsSource324(): void {
  const map = mapStore.myMap?.map
  if (!map || map.getSource(insidePointsSourceId.value)) return
  map.addSource(insidePointsSourceId.value, {
    type: 'geojson',
    data: insidePoints.value,
    cluster: true,
    clusterMaxZoom: CLUSTER_MAX_ZOOM,
    clusterRadius: CLUSTER_RADIUS,
    clusterProperties: buildClusterPropertiesForDonut() as Record<string, maplibregl.Expression>,
  })
}

async function ensureInsidePointsLayer(): Promise<void> {
  const map = mapStore.myMap?.map
  if (!map || !props.isActive) {
    return
  }

  if (isTypeOuvrageDonutIndicator.value) {
    ensureInsidePointsSource324()
    if (!map.getLayer(insidePointsClusterQueryLayerId.value)) {
      map.addLayer({
        id: insidePointsClusterQueryLayerId.value,
        type: 'circle',
        source: insidePointsSourceId.value,
        filter: ['has', 'point_count'],
        minzoom: 0,
        maxzoom: ZOOM_THRESHOLD_1_4000,
        paint: {
          'circle-radius': 1,
          'circle-opacity': 0,
        },
        metadata: { isPersistent: false },
      })
    }
    if (!isIndicator322.value) {
      await ensureTypeOuvrageMarkerResult324()
    }
    ensureClusterDonutMarkers324()
    if (!props.isActive) return
    ensureInsidePointsSource324()
    if (!map.getLayer(insidePointsSymbolLayerId.value)) {
      const fallbackIcon324 = isIndicator322.value
        ? `marker-322-badge-PUITS-lt100`
        : (typeOuvrageMarkerResult.value?.defaultImageId ?? INVENTORY_PICTO_IMAGE_324)
      map.addLayer({
        id: insidePointsSymbolLayerId.value,
        type: 'symbol',
        source: insidePointsSourceId.value,
        filter: ['!', ['has', 'point_count']],
        minzoom: ZOOM_THRESHOLD_1_4000,
        layout: {
          'icon-image': ['coalesce', ['get', 'iconImage'], fallbackIcon324],
          'icon-size': 0.6,
          'icon-allow-overlap': true,
          'icon-ignore-placement': false,
          'icon-anchor': 'bottom',
        },
        metadata: { isPersistent: false },
      })
    }
    if (!map.getLayer(insidePointsTextLayerId.value)) {
      map.addLayer({
        id: insidePointsTextLayerId.value,
        type: 'symbol',
        source: insidePointsSourceId.value,
        filter: ['!', ['has', 'point_count']],
        minzoom: ZOOM_THRESHOLD_1_4000,
        layout: {
          'text-field': ['coalesce', ['get', 'id'], ''],
          'text-size': 13,
          'text-offset': [0, 0.2],
          'text-anchor': 'top',
          'text-allow-overlap': false,
          'text-ignore-placement': false,
        },
        paint: {
          'text-color': '#333333',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.2,
        },
        metadata: { isPersistent: false },
      })
    }
    if (!clickHandler324) {
      clickHandler324 = (e: MapClickEvent) => {
        void handle324SymbolClick(e)
      }
      map.on('click', insidePointsSymbolLayerId.value, clickHandler324)
    }
    if (!symbolMouseEnterHandler324) {
      symbolMouseEnterHandler324 = () => {
        map.getCanvas().style.cursor = 'pointer'
      }
      map.on('mouseenter', insidePointsSymbolLayerId.value, symbolMouseEnterHandler324)
    }
    if (!symbolMouseLeaveHandler324) {
      symbolMouseLeaveHandler324 = () => {
        map.getCanvas().style.cursor = ''
      }
      map.on('mouseleave', insidePointsSymbolLayerId.value, symbolMouseLeaveHandler324)
    }
  } else if (isIndicator328.value) {
    if (!map.getSource(insidePointsSourceId.value)) {
      map.addSource(insidePointsSourceId.value, {
        type: 'geojson',
        data: insidePoints.value,
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: CLUSTER_RADIUS,
      })
    }
    if (!map.getLayer(insidePointsClusterQueryLayerId.value)) {
      map.addLayer({
        id: insidePointsClusterQueryLayerId.value,
        type: 'circle',
        source: insidePointsSourceId.value,
        filter: ['has', 'point_count'],
        minzoom: 0,
        maxzoom: ZOOM_THRESHOLD_1_4000,
        paint: {
          'circle-radius': 1,
          'circle-opacity': 0,
        },
        metadata: { isPersistent: false },
      })
    }
    ensureClusterSimpleMarkers328()
    if (!map.getSource(insidePointsSourceId.value)) {
      map.addSource(insidePointsSourceId.value, {
        type: 'geojson',
        data: insidePoints.value,
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: CLUSTER_RADIUS,
      })
    }
    if (!map.getLayer(insidePointsSymbolLayerId.value)) {
      map.addLayer({
        id: insidePointsSymbolLayerId.value,
        type: 'symbol',
        source: insidePointsSourceId.value,
        filter: ['!', ['has', 'point_count']],
        minzoom: ZOOM_THRESHOLD_1_4000,
        layout: {
          'icon-image': ['coalesce', ['get', 'iconImage'], MARKER_328_RED],
          'icon-size': 0.6,
          'icon-allow-overlap': true,
          'icon-ignore-placement': false,
          'icon-anchor': 'bottom',
        },
        metadata: { isPersistent: false },
      })
    }
    if (!map.getLayer(insidePointsTextLayerId.value)) {
      map.addLayer({
        id: insidePointsTextLayerId.value,
        type: 'symbol',
        source: insidePointsSourceId.value,
        filter: ['!', ['has', 'point_count']],
        minzoom: ZOOM_THRESHOLD_1_4000,
        layout: {
          'text-field': ['coalesce', ['get', DATE_CONSTRUCTION_PROPERTY], ''],
          'text-size': 13,
          'text-offset': [0, 0.2],
          'text-anchor': 'top',
          'text-allow-overlap': false,
          'text-ignore-placement': false,
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.2,
        },
        metadata: { isPersistent: false },
      })
    }
    if (!clickHandler328) {
      clickHandler328 = (e: MapClickEvent) => {
        void handle328SymbolClick(e)
      }
      map.on('click', insidePointsSymbolLayerId.value, clickHandler328)
    }
  } else if (isIndicator329.value) {
    if (!map.getSource(insidePointsSourceId.value)) {
      map.addSource(insidePointsSourceId.value, {
        type: 'geojson',
        data: insidePoints.value,
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: CLUSTER_329_RADIUS,
      })
    }
    if (!map.getLayer(insidePointsClusterQueryLayerId.value)) {
      map.addLayer({
        id: insidePointsClusterQueryLayerId.value,
        type: 'circle',
        source: insidePointsSourceId.value,
        filter: ['has', 'point_count'],
        minzoom: 0,
        maxzoom: ZOOM_THRESHOLD_1_4000,
        paint: {
          'circle-radius': 1,
          'circle-opacity': 0,
        },
        metadata: { isPersistent: false },
      })
    }
    ensureClusterSimpleMarkers329()
    if (!map.getSource(insidePointsSourceId.value)) {
      map.addSource(insidePointsSourceId.value, {
        type: 'geojson',
        data: insidePoints.value,
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: CLUSTER_RADIUS,
      })
    }
    if (!map.getLayer(insidePointsSymbolLayerId.value)) {
      map.addLayer({
        id: insidePointsSymbolLayerId.value,
        type: 'symbol',
        source: insidePointsSourceId.value,
        filter: ['!', ['has', 'point_count']],
        minzoom: ZOOM_THRESHOLD_1_4000,
        layout: {
          'icon-image': [
            'coalesce',
            ['get', 'iconImage'],
            getMarker329ImageName(UNKNOWN_TYPE_LABEL, 'N/A'),
          ],
          'icon-size': 0.6,
          'icon-allow-overlap': true,
          'icon-ignore-placement': false,
          'icon-anchor': 'bottom',
        },
        metadata: { isPersistent: false },
      })
    }
    if (!map.getLayer(insidePointsTextLayerId.value)) {
      map.addLayer({
        id: insidePointsTextLayerId.value,
        type: 'symbol',
        source: insidePointsSourceId.value,
        filter: ['!', ['has', 'point_count']],
        minzoom: ZOOM_THRESHOLD_1_4000,
        layout: {
          'text-field': ['coalesce', ['get', VALEUR_CALCULEE_PROPERTY], ''],
          'text-size': 13,
          'text-offset': [0, 0.2],
          'text-anchor': 'top',
          'text-allow-overlap': false,
          'text-ignore-placement': false,
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.2,
        },
        metadata: { isPersistent: false },
      })
    }
    if (!clickHandler329) {
      clickHandler329 = (e: MapClickEvent) => {
        void handle329SymbolClick(e)
      }
      map.on('click', insidePointsSymbolLayerId.value, clickHandler329)
    }
  } else {
    if (!map.getSource(insidePointsSourceId.value)) {
      map.addSource(insidePointsSourceId.value, {
        type: 'geojson',
        data: insidePoints.value,
      })
    }
    if (!map.getLayer(insidePointsLayerId.value)) {
      map.addLayer({
        id: insidePointsLayerId.value,
        type: 'circle',
        source: insidePointsSourceId.value,
        paint: {
          'circle-radius': 4,
          'circle-color': ['coalesce', ['get', 'color'], '#144d7e'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1,
        },
        metadata: { isPersistent: false },
      })
    }
  }
}

function updateInsidePointsSource() {
  const map = mapStore.myMap?.map
  if (!map || !map.getSource(insidePointsSourceId.value)) {
    return
  }
  const source = map.getSource(insidePointsSourceId.value) as maplibregl.GeoJSONSource
  source.setData(insidePoints.value)
}

function updateCircleLayers() {
  const map = mapStore.myMap?.map
  if (!map || !circleCenter.value) {
    return
  }

  const radiusKm = radiusMetersValue.value / METERS_PER_KILOMETER
  circlePolygon.value = turfCircle([circleCenter.value.lng, circleCenter.value.lat], radiusKm, {
    steps: 64,
    units: 'kilometers',
  }) as Feature<Polygon>

  if (!map.getSource(circleSourceId.value)) {
    map.addSource(circleSourceId.value, {
      type: 'geojson',
      data: circlePolygon.value,
    })
  } else {
    const geoJsonSource = map.getSource(circleSourceId.value) as maplibregl.GeoJSONSource
    geoJsonSource.setData(circlePolygon.value)
  }

  if (map.getLayer(circleOutlineId.value)) {
    map.removeLayer(circleOutlineId.value)
  }
}

function scheduleCircleUpdate() {
  if (dragAnimationFrame.value !== null) {
    cancelAnimationFrame(dragAnimationFrame.value)
  }
  dragAnimationFrame.value = requestAnimationFrame(() => {
    dragAnimationFrame.value = null
    updateCircleLayers()
  })
}

// Reserved for future circle-drag UX
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ensureDragMarker() {
  const map = mapStore.myMap?.map
  if (!map || !circleCenter.value || dragMarker.value) {
    return
  }

  const dragElement = document.createElement('button')
  dragElement.type = 'button'
  dragElement.className = 'InventoryCircleAnalysis__dragMarker'
  dragElement.setAttribute('aria-label', t('myMap.inventoryCircle.moveCenter'))

  const marker = new maplibregl.Marker({
    element: dragElement,
    draggable: true,
  })
    .setLngLat(circleCenter.value)
    .addTo(map)

  dragMarker.value = marker

  const dragEventTarget = marker as unknown as {
    on: (event: string, handler: () => void) => void
  }

  dragEventTarget.on('drag', () => {
    const markerCenter = marker.getLngLat()
    if (!markerCenter) {
      return
    }
    circleCenter.value = markerCenter
    scheduleCircleUpdate()
  })

  dragEventTarget.on('dragend', () => {
    const markerCenter = marker.getLngLat()
    if (!markerCenter) {
      return
    }
    circleCenter.value = markerCenter
    void updateAggregation()
  })
}

// Reserved for future circle-drag UX
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function attachCircleDragHandlers() {
  const map = mapStore.myMap?.map
  if (!map || mapMouseMoveHandler || mapMouseUpHandler || mapAnchorHoverHandler) {
    return
  }

  map.on('mousedown', handleCircleMouseDown)
  mapAnchorHoverHandler = (event) => {
    if (!props.isActive) {
      if (isHoveringAnchor.value) {
        isHoveringAnchor.value = false
        map.getCanvas().style.cursor = ''
      }
      return
    }

    const baseRadiusPixels = getBaseRadiusPixels(map)
    if (baseRadiusPixels === null || !circleCenter.value) {
      if (isHoveringAnchor.value) {
        isHoveringAnchor.value = false
        map.getCanvas().style.cursor = ''
      }
      return
    }

    const centerPoint = map.project(circleCenter.value)
    const anchorWidth = 30
    const anchorHeight = 12
    const anchorLeft = centerPoint.x - anchorWidth / 2
    const anchorTop = centerPoint.y + baseRadiusPixels + 8
    const anchorRight = anchorLeft + anchorWidth
    const anchorBottom = anchorTop + anchorHeight
    const isOnAnchor =
      event.point.x >= anchorLeft &&
      event.point.x <= anchorRight &&
      event.point.y >= anchorTop &&
      event.point.y <= anchorBottom

    if (isOnAnchor && !isHoveringAnchor.value) {
      isHoveringAnchor.value = true
      if (!isResizingCircle.value && !isDraggingCircle.value) {
        map.getCanvas().style.cursor = 'ns-resize'
      }
      console.log('[InventoryCircleAnalysis] Anchor hover')
      return
    }

    if (!isOnAnchor && isHoveringAnchor.value) {
      isHoveringAnchor.value = false
      if (!isResizingCircle.value && !isDraggingCircle.value) {
        map.getCanvas().style.cursor = ''
      }
    }
  }

  map.on('mousemove', mapAnchorHoverHandler)
}

function detachCircleDragHandlers() {
  const map = mapStore.myMap?.map
  if (!map) {
    return
  }

  map.off('mousedown', handleCircleMouseDown)
  if (mapAnchorHoverHandler) {
    map.off('mousemove', mapAnchorHoverHandler)
    mapAnchorHoverHandler = null
  }
  map.getCanvas().style.cursor = ''
  if (mapMouseMoveHandler) {
    map.off('mousemove', mapMouseMoveHandler)
    mapMouseMoveHandler = null
  }
  if (mapMouseUpHandler) {
    map.off('mouseup', mapMouseUpHandler)
    mapMouseUpHandler = null
  }
}

function handleCircleMouseDown(event: maplibregl.MapMouseEvent) {
  const map = mapStore.myMap?.map
  if (!map || !props.isActive) {
    return
  }

  const layersToQuery: string[] = []
  if (
    isIndicator322.value ||
    isIndicator324.value ||
    isIndicator328.value ||
    isIndicator329.value
  ) {
    if (map.getLayer(insidePointsSymbolLayerId.value)) {
      layersToQuery.push(insidePointsSymbolLayerId.value)
    }
  } else if (map.getLayer(insidePointsLayerId.value)) {
    layersToQuery.push(insidePointsLayerId.value)
  }
  if (layersToQuery.length > 0) {
    const insidePointFeatures = map.queryRenderedFeatures(event.point, {
      layers: layersToQuery,
    })
    if (insidePointFeatures.length > 0) {
      return
    }
  }

  updateScaleDenominator()
  const baseRadiusPixels = getBaseRadiusPixels(map)
  if (baseRadiusPixels !== null) {
    const centerPoint = map.project(circleCenter.value as maplibregl.LngLat)
    const anchorWidth = 30
    const anchorHeight = 12
    const anchorLeft = centerPoint.x - anchorWidth / 2
    const anchorTop = centerPoint.y + baseRadiusPixels + 8
    const anchorRight = anchorLeft + anchorWidth
    const anchorBottom = anchorTop + anchorHeight
    const isOnAnchor =
      event.point.x >= anchorLeft &&
      event.point.x <= anchorRight &&
      event.point.y >= anchorTop &&
      event.point.y <= anchorBottom
    if (isOnAnchor) {
      event.preventDefault()
      event.originalEvent?.stopPropagation()
      startCircleResize()
      return
    }
  }

  if (!circlePolygon.value) {
    return
  }

  const isInsideCircle = booleanPointInPolygon(
    {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: [event.lngLat.lng, event.lngLat.lat],
      },
    },
    circlePolygon.value,
  )

  if (!isInsideCircle) {
    return
  }

  event.preventDefault()
  isDraggingCircle.value = true
  map.dragPan.disable()
  map.getCanvas().style.cursor = 'grabbing'

  mapMouseMoveHandler = (moveEvent) => {
    if (!isDraggingCircle.value) {
      return
    }
    circleCenter.value = moveEvent.lngLat
    scheduleCircleUpdate()
    // Overlay disabled - no need to update
  }

  mapMouseUpHandler = (upEvent) => {
    if (!isDraggingCircle.value) {
      return
    }
    isDraggingCircle.value = false
    circleCenter.value = upEvent.lngLat
    map.dragPan.enable()
    map.getCanvas().style.cursor = ''
    if (mapMouseMoveHandler) {
      map.off('mousemove', mapMouseMoveHandler)
      mapMouseMoveHandler = null
    }
    if (mapMouseUpHandler) {
      map.off('mouseup', mapMouseUpHandler)
      mapMouseUpHandler = null
    }
    void updateAggregation()
    // Overlay disabled - no need to update
  }

  map.on('mousemove', mapMouseMoveHandler)
  map.on('mouseup', mapMouseUpHandler)
}

function startCircleResize() {
  const map = mapStore.myMap?.map
  if (!map || !circleCenter.value) {
    return
  }

  updateScaleDenominator()
  isResizingCircle.value = true
  map.dragPan.disable()
  map.getCanvas().style.cursor = 'ns-resize'

  mapResizeMoveHandler = (moveEvent) => {
    if (!isResizingCircle.value || !circleCenter.value) {
      return
    }
    const centerPoint = map.project(circleCenter.value)
    const distancePixels = Math.hypot(
      moveEvent.point.x - centerPoint.x,
      moveEvent.point.y - centerPoint.y,
    )
    const metersPerPixel = getMetersPerPixel()
    if (!metersPerPixel) {
      return
    }
    const targetMeters = Math.round(distancePixels * metersPerPixel)
    const clampedMeters = Math.min(MAX_RADIUS_METERS, Math.max(MIN_RADIUS_METERS, targetMeters))
    if (clampedMeters !== radiusMetersValue.value) {
      radiusMetersValue.value = clampedMeters
    } else {
      updateCircleLayers()
      // Overlay disabled - no need to update
    }
    // Overlay disabled - no need to update
  }

  mapResizeUpHandler = () => {
    if (!isResizingCircle.value) {
      return
    }
    isResizingCircle.value = false
    map.dragPan.enable()
    map.getCanvas().style.cursor = ''
    if (mapResizeMoveHandler) {
      map.off('mousemove', mapResizeMoveHandler)
      mapResizeMoveHandler = null
    }
    if (mapResizeUpHandler) {
      map.off('mouseup', mapResizeUpHandler)
      mapResizeUpHandler = null
    }
    // Always update aggregation - zoom restriction removed
    void updateAggregation()
  }

  map.on('mousemove', mapResizeMoveHandler)
  map.on('mouseup', mapResizeUpHandler)
}

function getBaseRadiusPixels(map: maplibregl.Map): number | null {
  if (!circleCenter.value) {
    return null
  }
  const metersPerPixel = getMetersPerPixel()
  if (metersPerPixel) {
    return radiusMetersValue.value / metersPerPixel
  }
  if (!circlePolygon.value) {
    return null
  }
  const centerPoint = map.project(circleCenter.value)
  const boundaryCoordinate = circlePolygon.value.geometry.coordinates[0][0]
  const boundaryPoint = map.project(boundaryCoordinate as [number, number])
  return Math.hypot(boundaryPoint.x - centerPoint.x, boundaryPoint.y - centerPoint.y)
}

function removeCircleLayers() {
  const map = mapStore.myMap?.map
  if (!map) {
    return
  }

  if (map.getLayer(queryLayerId.value)) {
    map.removeLayer(queryLayerId.value)
  }
  if (map.getLayer(circleFillId.value)) {
    map.removeLayer(circleFillId.value)
  }
  if (map.getLayer(circleMaskFillId.value)) {
    map.removeLayer(circleMaskFillId.value)
  }
  if (map.getLayer(circleOutlineId.value)) {
    map.removeLayer(circleOutlineId.value)
  }
  if (isTypeOuvrageDonutIndicator.value) {
    if (clickHandler324) {
      map.off('click', insidePointsSymbolLayerId.value, clickHandler324)
      clickHandler324 = null
    }
    if (symbolMouseEnterHandler324) {
      map.off('mouseenter', insidePointsSymbolLayerId.value, symbolMouseEnterHandler324)
      symbolMouseEnterHandler324 = null
    }
    if (symbolMouseLeaveHandler324) {
      map.off('mouseleave', insidePointsSymbolLayerId.value, symbolMouseLeaveHandler324)
      symbolMouseLeaveHandler324 = null
    }
    map.getCanvas().style.cursor = ''
    if (clusterDonutRenderHandler324) {
      map.off('render', clusterDonutRenderHandler324)
      clusterDonutRenderHandler324 = null
    }
    if (clusterDonutDataHandler324) {
      map.off('data', clusterDonutDataHandler324)
      clusterDonutDataHandler324 = null
    }
    const screenIds = Object.keys(clusterDonutMarkersOnScreen324).map(Number)
    for (const id of screenIds) {
      clusterDonutMarkersOnScreen324[id].remove()
      delete clusterDonutMarkersOnScreen324[id]
    }
    const cacheIds = Object.keys(clusterDonutMarkers324).map(Number)
    for (const id of cacheIds) {
      clusterDonutMarkers324[id].remove()
      delete clusterDonutMarkers324[id]
    }
    if (map.getLayer(insidePointsTextLayerId.value)) {
      map.removeLayer(insidePointsTextLayerId.value)
    }
    if (map.getLayer(insidePointsSymbolLayerId.value)) {
      map.removeLayer(insidePointsSymbolLayerId.value)
    }
    if (map.getLayer(insidePointsClusterQueryLayerId.value)) {
      map.removeLayer(insidePointsClusterQueryLayerId.value)
    }
    if (isIndicator322.value) {
      const typeCodes322 = Object.keys(TYPE_OUVRAGE_TO_ICON_KEY)
      for (const typeCode of typeCodes322) {
        for (const valueKey of COMPLETION_322_VALUE_KEYS) {
          const imageName = `marker-322-badge-${typeCode}-${valueKey}`
          if (map.hasImage(imageName)) {
            map.removeImage(imageName)
          }
        }
      }
    } else {
      if (map.hasImage(INVENTORY_PICTO_IMAGE_324)) {
        map.removeImage(INVENTORY_PICTO_IMAGE_324)
      }
      for (const typeOuvrage of Object.keys(TYPE_OUVRAGE_TO_ICON_KEY)) {
        const imageName = getPictoImageName324(typeOuvrage)
        if (map.hasImage(imageName)) {
          map.removeImage(imageName)
        }
      }
    }
    typeOuvrageMarkerResult.value = null
  } else if (isIndicator328.value) {
    if (clickHandler328) {
      map.off('click', insidePointsSymbolLayerId.value, clickHandler328)
      clickHandler328 = null
    }
    if (map.getLayer(insidePointsTextLayerId.value)) {
      map.removeLayer(insidePointsTextLayerId.value)
    }
    if (clusterSimpleRenderHandler328) {
      map.off('render', clusterSimpleRenderHandler328)
      clusterSimpleRenderHandler328 = null
    }
    if (clusterSimpleDataHandler328) {
      map.off('data', clusterSimpleDataHandler328)
      clusterSimpleDataHandler328 = null
    }
    const screenIds328 = Object.keys(clusterSimpleMarkersOnScreen328).map(Number)
    for (const id of screenIds328) {
      clusterSimpleMarkersOnScreen328[id].remove()
      delete clusterSimpleMarkersOnScreen328[id]
    }
    const cacheIds328 = Object.keys(clusterSimpleMarkers328).map(Number)
    for (const id of cacheIds328) {
      clusterSimpleMarkers328[id].remove()
      delete clusterSimpleMarkers328[id]
    }
    if (map.getLayer(insidePointsSymbolLayerId.value)) {
      map.removeLayer(insidePointsSymbolLayerId.value)
    }
    if (map.getLayer(insidePointsClusterQueryLayerId.value)) {
      map.removeLayer(insidePointsClusterQueryLayerId.value)
    }
    if (map.hasImage(MARKER_328_GREEN)) {
      map.removeImage(MARKER_328_GREEN)
    }
    if (map.hasImage(MARKER_328_RED)) {
      map.removeImage(MARKER_328_RED)
    }
  } else if (isIndicator329.value) {
    if (clickHandler329) {
      map.off('click', insidePointsSymbolLayerId.value, clickHandler329)
      clickHandler329 = null
    }
    if (map.getLayer(insidePointsTextLayerId.value)) {
      map.removeLayer(insidePointsTextLayerId.value)
    }
    if (clusterSimpleRenderHandler329) {
      map.off('render', clusterSimpleRenderHandler329)
      clusterSimpleRenderHandler329 = null
    }
    if (clusterSimpleDataHandler329) {
      map.off('data', clusterSimpleDataHandler329)
      clusterSimpleDataHandler329 = null
    }
    const screenIds329 = Object.keys(clusterSimpleMarkersOnScreen329).map(Number)
    for (const id of screenIds329) {
      clusterSimpleMarkersOnScreen329[id].remove()
      delete clusterSimpleMarkersOnScreen329[id]
    }
    const cacheIds329 = Object.keys(clusterSimpleMarkers329).map(Number)
    for (const id of cacheIds329) {
      clusterSimpleMarkers329[id].remove()
      delete clusterSimpleMarkers329[id]
    }
    if (map.getLayer(insidePointsSymbolLayerId.value)) {
      map.removeLayer(insidePointsSymbolLayerId.value)
    }
    if (map.getLayer(insidePointsClusterQueryLayerId.value)) {
      map.removeLayer(insidePointsClusterQueryLayerId.value)
    }
    const typeCodes329 = [...Object.keys(TYPE_OUVRAGE_TO_ICON_KEY), UNKNOWN_TYPE_LABEL]
    const valueKeys329 = [...VALUE_329_KEYS]
    for (const typeCode of typeCodes329) {
      for (const valueKey of valueKeys329) {
        const imageName = `marker-329-badge-${typeCode}-${valueKey}`
        if (map.hasImage(imageName)) {
          map.removeImage(imageName)
        }
      }
    }
  } else {
    if (map.getLayer(insidePointsLayerId.value)) {
      map.removeLayer(insidePointsLayerId.value)
    }
  }
  if (map.getSource(circleSourceId.value)) {
    map.removeSource(circleSourceId.value)
  }
  if (map.getSource(insidePointsSourceId.value)) {
    map.removeSource(insidePointsSourceId.value)
  }
  if (map.getSource(circleMaskSourceId.value)) {
    map.removeSource(circleMaskSourceId.value)
  }
  circlePolygon.value = null
  insidePoints.value = { type: 'FeatureCollection', features: [] }
}

function detachDragMarker() {
  if (dragMarker.value) {
    const marker = dragMarker.value as maplibregl.Marker
    marker.remove()
    dragMarker.value = null
  }
  if (dragAnimationFrame.value !== null) {
    cancelAnimationFrame(dragAnimationFrame.value)
    dragAnimationFrame.value = null
  }
}

// buildPointFeatures removed: queryRenderedFeatures is used for aggregation

async function updateAggregation() {
  if (!props.isActive) return
  const map = mapStore.myMap?.map
  if (!map || !isZoomAllowed.value) {
    // Clear points if zoom is insufficient
    if (!isZoomAllowed.value) {
      insidePoints.value = { type: 'FeatureCollection', features: [] }
      aggregationMap.value = {}
      updateInsidePointsSource()
    }
    return
  }

  try {
    ensureVectorSource()
    ensureQueryLayer()
    if (!map.getLayer(queryLayerId.value)) {
      return
    }
    // Get all visible points on the map (no circular filter)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for bounds filtering
    const mapBounds = map.getBounds()
    const screenBounds: [maplibregl.PointLike, maplibregl.PointLike] = [
      [0, 0],
      [map.getCanvas().width, map.getCanvas().height],
    ]

    const renderedFeatures = map.queryRenderedFeatures(screenBounds, {
      layers: [queryLayerId.value],
    })

    const points = renderedFeatures.filter(isPointFeature).map((feature) => {
      const [lng, lat] = feature.geometry.coordinates as [number, number]
      const props_ = feature.properties ?? {}
      const idDisplay =
        feature.id ?? props_.ift_id ?? props_.id ?? props_.gid ?? props_.ogc_fid ?? ''
      const idStr =
        idDisplay !== undefined && idDisplay !== null
          ? typeof idDisplay === 'number'
            ? String(idDisplay)
            : String(idDisplay)
          : ''
      const typeValue = normalizeTypeValue(props_[props.typePropertyName], UNKNOWN_TYPE_LABEL)
      const completionBucket322 = getCompletionBucketFromValue322(
        props_.pourc_completude_fiche ?? props_.valeur_calculee,
      )
      const dateVal = props_[props.typePropertyName]
      const isDateFilled = isDateFilledFor328(dateVal)
      const typeValue328 = isDateFilled ? t('myMap.inventoryCircle.recorded') : 'N/A'
      const iconImage328 = isDateFilled ? MARKER_328_GREEN : MARKER_328_RED
      const maintenanceBucket329 = getMaintenanceBucketFromValue329(props_[props.typePropertyName])
      const typeValue329 = maintenanceBucket329
      const rawTypeOuvrage = props_.type_ouvrage ?? props_.TYPE_OUVRAGE ?? typeValue
      const typeOuvrage322 = normalizeTypeOuvrageKey(rawTypeOuvrage)
      const typeOuvrage329 = typeOuvrage322
      const iconImage329 = getMarker329ImageName(typeOuvrage329, maintenanceBucket329)
      const baseProperties = isTypeOuvrageDonutIndicator.value
        ? {
            ...props_,
            typeValue: isIndicator322.value ? typeOuvrage322 : typeValue,
            id: idStr,
            iconImage: isIndicator322.value
              ? getMarker322ImageName(typeOuvrage322, completionBucket322)
              : (typeOuvrageMarkerResult.value?.typeToImageId[typeValue] ??
                typeOuvrageMarkerResult.value?.defaultImageId ??
                getPictoImageName324(typeValue)),
          }
        : isIndicator328.value
          ? { ...props_, typeValue: typeValue328, id: idStr, iconImage: iconImage328 }
          : isIndicator329.value
            ? { ...props_, typeValue: typeValue329, id: idStr, iconImage: iconImage329 }
            : { typeValue, id: idStr }
      const pointFeature: Feature<
        Point,
        { typeValue: string; id?: string; color?: string; iconImage?: string } & Record<
          string,
          unknown
        >
      > = {
        type: 'Feature',
        properties: baseProperties as {
          typeValue: string
          id?: string
          color?: string
          iconImage?: string
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
      }
      return pointFeature
    })

    // Use all points without circular filtering
    const allPoints: FeatureCollection<Point> = {
      type: 'FeatureCollection',
      features: points,
    }

    if (points.length === 0 && isTypeOuvrageLayer.value) {
      console.info(
        '[InventoryCircleAnalysis] No point features from query layer. Check: (1) zoom level (individual markers show at zoom >= 15), (2) vector tiles for view',
        props.viewLayerName,
        'loading without errors.',
      )
    }

    const counts: Record<string, number> = isIndicator329.value
      ? Object.fromEntries(MAINTENANCE_329_BUCKETS.map((bucket) => [bucket, 0]))
      : {}
    for (const feature of allPoints.features) {
      const typeValue = feature.properties?.typeValue as string
      counts[typeValue] = (counts[typeValue] ?? 0) + 1
    }
    const orderedTypes = Object.entries(counts)
      .map(([typeLabel, count]) => ({ typeLabel, count }))
      .sort((a, b) => b.count - a.count || a.typeLabel.localeCompare(b.typeLabel))
      .map((entry) => entry.typeLabel)
    const colorMap = isIndicator328.value
      ? { Renseigné: MARKER_328_GREEN_COLOR, 'N/A': MARKER_328_RED_COLOR }
      : isIndicator322.value
        ? {
            ...Object.fromEntries(
              Object.keys(TYPE_OUVRAGE_TO_ICON_KEY).map((typeLabel) => [
                typeLabel,
                getColorForTypeOuvrage(typeLabel),
              ]),
            ),
          }
      : isIndicator329.value
        ? {
            'N/A': MARKER_329_NA_COLOR,
            '< 1 an': MARKER_329_LT_1_COLOR,
            '< 2 ans': MARKER_329_LT_2_COLOR,
            '< 5 ans': MARKER_329_LT_5_COLOR,
            '>= 5 ans': MARKER_329_GE_5_COLOR,
          }
        : orderedTypes.reduce<Record<string, string>>((accumulator, typeLabel) => {
            accumulator[typeLabel] = getColorForTypeOuvrage(typeLabel)
            return accumulator
          }, {})

    aggregationMap.value = counts
    // Overlay disabled - no need to update

    const visibleFeatures = isIndicator329.value
      ? allPoints.features.filter((feature) => {
          const bucketLabel = String(feature.properties?.typeValue)
          return isMaintenance329Bucket(bucketLabel)
            ? selectedMaintenance329Buckets.value[bucketLabel]
            : true
        })
      : allPoints.features

    // Display all points without circular filter
    insidePoints.value = {
      type: 'FeatureCollection',
      features: visibleFeatures.map((feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          color: colorMap[feature.properties?.typeValue as string],
        },
      })),
    }
    if (isTypeOuvrageDonutIndicator.value) {
      if (isIndicator322.value) {
        await ensureMarkerImages322()
      } else {
        await ensurePictoImages324()
      }
    }
    if (isIndicator328.value) {
      await ensureMarkerImages328()
    }
    if (isIndicator329.value) {
      await ensureMarkerImages329()
    }
    await ensureInsidePointsLayer()
    updateInsidePointsSource()
  } catch (error) {
    console.error('[InventoryCircleAnalysis] Error aggregating infrastructure data:', error)
  }
}

function attachIdleListener() {
  const map = mapStore.myMap?.map
  if (!map || mapIdleHandler) {
    return
  }

  mapIdleHandler = () => {
    if (!props.isActive) {
      return
    }
    // Always update aggregation - zoom restriction removed
    void updateAggregation()
    // Overlay disabled - no need to update
  }

  map.on('idle', mapIdleHandler)
}

function detachIdleListener() {
  const map = mapStore.myMap?.map
  if (!map || !mapIdleHandler) {
    return
  }
  map.off('idle', mapIdleHandler)
  mapIdleHandler = null
}

function attachMoveListener() {
  const map = mapStore.myMap?.map
  if (!map || mapMoveHandler) {
    return
  }

  mapMoveHandler = () => {
    if (!props.isActive) {
      return
    }
    if (isDraggingCircle.value) {
      return
    }
    updateScaleDenominator()
    // Update aggregation only on moveend, not during move
    void updateAggregation()
  }

  map.on('moveend', mapMoveHandler)
}

function detachMoveListener() {
  const map = mapStore.myMap?.map
  if (!map || !mapMoveHandler) {
    return
  }
  map.off('moveend', mapMoveHandler)
  mapMoveHandler = null
}

function attachZoomListener() {
  const map = mapStore.myMap?.map
  if (!map || mapZoomStartHandler || mapZoomEndHandler) {
    return
  }

  mapZoomStartHandler = () => {
    if (!props.isActive) {
      return
    }
    // Overlay disabled - no need to set visibility
  }

  mapZoomEndHandler = () => {
    if (!props.isActive) {
      return
    }
    updateScaleDenominator()
    void updateAggregation()
  }

  map.on('zoomstart', mapZoomStartHandler)
  map.on('zoomend', mapZoomEndHandler)
}

function detachZoomListener() {
  const map = mapStore.myMap?.map
  if (!map) {
    return
  }
  if (mapZoomStartHandler) {
    map.off('zoomstart', mapZoomStartHandler)
    mapZoomStartHandler = null
  }
  if (mapZoomEndHandler) {
    map.off('zoomend', mapZoomEndHandler)
    mapZoomEndHandler = null
  }
}

function normalizeTypeValue(rawValue: unknown, fallbackLabel: string): string {
  if (rawValue === null || rawValue === undefined) {
    return fallbackLabel
  }
  if (typeof rawValue === 'string' && rawValue.trim().length > 0) {
    return rawValue
  }
  if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {
    return String(rawValue)
  }
  return fallbackLabel
}

function isPointGeometry(geometry: GeoJSON.Geometry | null | undefined): geometry is Point {
  return geometry?.type === 'Point' && Array.isArray((geometry as Point).coordinates)
}

function isPointFeature(
  feature: MapGeoJSONFeature,
): feature is MapGeoJSONFeature & { geometry: Point } {
  return isPointGeometry(feature.geometry)
}

function normalizeSourceLayerName(layerName: string): string {
  return layerName.includes('.') ? layerName.split('.').pop() || layerName : layerName
}

function formatLabelName(label: string): string {
  return label.replace(/_/g, '\n').toUpperCase()
}

function formatLabelNameForPanel(label: string): string {
  return label.replace(/_/g, ' ').toUpperCase()
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for overlay/bounds logic
function getPolygonBoundingBox(polygonFeature: Feature<Polygon>): {
  minLng: number
  minLat: number
  maxLng: number
  maxLat: number
} {
  const coordinates = polygonFeature.geometry.coordinates[0]
  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity

  for (const [lng, lat] of coordinates) {
    if (lng < minLng) minLng = lng
    if (lat < minLat) minLat = lat
    if (lng > maxLng) maxLng = lng
    if (lat > maxLat) maxLat = lat
  }

  return { minLng, minLat, maxLng, maxLat }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- overlay disabled, bars in panel
function updateOverlayChart() {
  const map = mapStore.myMap?.map
  if (!map || !overlaySvg || !circleCenter.value) {
    return
  }

  // Calculate circle polygon if not available (for chart positioning)
  if (!circlePolygon.value) {
    const radiusKm = radiusMetersValue.value / METERS_PER_KILOMETER
    circlePolygon.value = turfCircle([circleCenter.value.lng, circleCenter.value.lat], radiusKm, {
      steps: 64,
      units: 'kilometers',
    }) as Feature<Polygon>
  }

  const mapCanvas = map.getCanvas()
  const { width, height } = mapCanvas.getBoundingClientRect()
  overlaySvg.setAttribute('width', `${width}`)
  overlaySvg.setAttribute('height', `${height}`)

  const svgSelection = select(overlaySvg)
  const entries = aggregationEntries.value
  if (entries.length === 0) {
    svgSelection.selectAll('*').remove()
    return
  }

  const entriesCount = Math.max(entries.length, 1)
  const maxValue = max(entries.map((entry) => entry.count)) ?? 1
  const centerPoint = map.project(circleCenter.value)
  const boundaryCoordinate = circlePolygon.value.geometry.coordinates[0][0]
  const boundaryPoint = map.project(boundaryCoordinate as [number, number])
  updateScaleDenominator()
  const metersPerPixel = getMetersPerPixel()
  const fallbackRadius = Math.hypot(
    boundaryPoint.x - centerPoint.x,
    boundaryPoint.y - centerPoint.y,
  )
  const baseRadius = metersPerPixel ? radiusMetersValue.value / metersPerPixel : fallbackRadius
  const safeRadius = Math.max(4, baseRadius)
  const angleScale = scaleLinear()
    .domain([0, entriesCount - 1])
    .range([OVERLAY_START_ANGLE, OVERLAY_END_ANGLE])
  const lengthScale = scaleLinear().domain([0, maxValue]).range([0, OVERLAY_BAR_MAX_LENGTH])
  const overlayTransition = transition('overlay-bars').duration(OVERLAY_TRANSITION_MS)
  const numberFormat = d3Format(',')

  const widgetSelection = (svgSelection.select('g.InventoryCircleAnalysis__radialWidget').empty()
    ? svgSelection.append('g').attr('class', 'InventoryCircleAnalysis__radialWidget')
    : svgSelection.select('g.InventoryCircleAnalysis__radialWidget')) as unknown as Selection<
    SVGGElement,
    unknown,
    SVGSVGElement,
    unknown
  >

  widgetSelection.attr('transform', `translate(${centerPoint.x}, ${centerPoint.y})`)
  widgetSelection.selectAll('circle.InventoryCircleAnalysis__ring').remove()

  const anchorSelection = (widgetSelection.select('rect.InventoryCircleAnalysis__anchor').empty()
    ? widgetSelection.append('rect').attr('class', 'InventoryCircleAnalysis__anchor')
    : widgetSelection.select('rect.InventoryCircleAnalysis__anchor')) as unknown as Selection<
    SVGRectElement,
    unknown,
    SVGGElement,
    unknown
  >

  anchorSelection
    .attr('width', 30)
    .attr('height', 15)
    .attr('x', -15)
    .attr('y', safeRadius + 5)
    .attr('rx', 3)
    .attr('fill', OVERLAY_ANCHOR_COLOR)
    .attr('opacity', OVERLAY_ANCHOR_OPACITY)

  const anchorIconSelection = (widgetSelection
    .select('text.InventoryCircleAnalysis__anchorIcon')
    .empty()
    ? widgetSelection.append('text').attr('class', 'InventoryCircleAnalysis__anchorIcon')
    : widgetSelection.select('text.InventoryCircleAnalysis__anchorIcon')) as unknown as Selection<
    SVGTextElement,
    unknown,
    SVGGElement,
    unknown
  >

  anchorIconSelection
    .attr('x', 0)
    .attr('y', safeRadius + 12.5)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .text('+')

  const barsGroup = (widgetSelection.select('g.InventoryCircleAnalysis__barsGroup').empty()
    ? widgetSelection.append('g').attr('class', 'InventoryCircleAnalysis__barsGroup')
    : widgetSelection.select('g.InventoryCircleAnalysis__barsGroup')) as unknown as Selection<
    SVGGElement,
    unknown,
    SVGGElement,
    unknown
  >

  const labelsGroup = (widgetSelection.select('g.InventoryCircleAnalysis__labelsGroup').empty()
    ? widgetSelection.append('g').attr('class', 'InventoryCircleAnalysis__labelsGroup')
    : widgetSelection.select('g.InventoryCircleAnalysis__labelsGroup')) as unknown as Selection<
    SVGGElement,
    unknown,
    SVGGElement,
    unknown
  >

  labelsGroup.selectAll('*').remove()

  const barGroups = barsGroup
    .selectAll('g.InventoryCircleAnalysis__barGroup')
    .data(
      entries,
      (entry: unknown) => (entry as AggregationEntry).typeLabel,
    ) as unknown as Selection<SVGGElement, AggregationEntry, SVGGElement, AggregationEntry>

  const barGroupsEnter = barGroups
    .enter()
    .append('g')
    .attr('class', 'InventoryCircleAnalysis__barGroup')
    .attr('transform', (_entry: AggregationEntry, index: number) => {
      return `rotate(${angleScale(index)})`
    })

  barGroupsEnter
    .append('rect')
    .attr('class', 'InventoryCircleAnalysis__overlayBar')
    .attr('x', -OVERLAY_BAR_WIDTH / 2)
    .attr('y', -safeRadius)
    .attr('width', OVERLAY_BAR_WIDTH)
    .attr('height', 0)
    .attr('rx', 4)
    .attr('fill', (entry: AggregationEntry) => {
      return getColorForLegendLabel(entry.typeLabel)
    })

  const barGroupsMerge = barGroupsEnter.merge(barGroups)

  barGroupsMerge
    .transition(overlayTransition)
    .attr('transform', (_entry: AggregationEntry, index: number) => {
      return `rotate(${angleScale(index)})`
    })

  barGroupsMerge
    .select('rect.InventoryCircleAnalysis__overlayBar')
    .transition(overlayTransition)
    .attr('fill', (entry: AggregationEntry) => {
      return getColorForLegendLabel(entry.typeLabel)
    })
    .attr('y', (entry: AggregationEntry) => -safeRadius - lengthScale(entry.count))
    .attr('height', (entry: AggregationEntry) => lengthScale(entry.count))

  const labelGroups = labelsGroup
    .selectAll('g.InventoryCircleAnalysis__barLabel')
    .data(
      entries,
      (entry: unknown) => (entry as AggregationEntry).typeLabel,
    ) as unknown as Selection<SVGGElement, AggregationEntry, SVGGElement, AggregationEntry>

  const labelGroupsEnter = labelGroups
    .enter()
    .append('g')
    .attr('class', 'InventoryCircleAnalysis__barLabel')

  labelGroupsEnter
    .append('text')
    .attr('class', 'InventoryCircleAnalysis__barLabelName')
    .attr('text-anchor', 'middle')

  labelGroupsEnter
    .append('line')
    .attr('class', 'InventoryCircleAnalysis__barLabelDivider')
    .attr('x1', -18)
    .attr('x2', 18)
    .attr('y1', 6)
    .attr('y2', 6)

  labelGroupsEnter
    .append('text')
    .attr('class', 'InventoryCircleAnalysis__barLabelValue')
    .attr('text-anchor', 'middle')
    .attr('y', 18)

  const labelGroupsMerge = labelGroupsEnter.merge(labelGroups)

  labelGroupsMerge.select('text.InventoryCircleAnalysis__barLabelName').each(function (
    this: SVGTextElement,
    entry: AggregationEntry,
    index: number,
  ) {
    const elementSelection = select(this)
    elementSelection.attr('y', 0)
    elementSelection.selectAll('tspan').remove()
    const lines = formatLabelName(entry.typeLabel).split('\n')
    lines.forEach((line, lineIndex) => {
      elementSelection
        .append('tspan')
        .attr('x', 0)
        .attr('dy', lineIndex === 0 ? 0 : OVERLAY_LABEL_LINE_HEIGHT)
        .text(line)
    })

    const groupElement = elementSelection.node()?.parentElement
    const nameBox = elementSelection.node()?.getBBox()
    if (!groupElement || !nameBox) {
      return
    }

    const angle = angleScale(index)
    const barLength = lengthScale(entry.count)
    const dynamicOffset =
      OVERLAY_VALUE_OFFSET + Math.max(0, lines.length - 1) * OVERLAY_LABEL_LINE_HEIGHT
    const radius = safeRadius + barLength + dynamicOffset
    groupElement.setAttribute('transform', `rotate(${angle}) translate(0, ${-radius})`)

    const dividerElement = groupElement.querySelector<SVGLineElement>(
      'line.InventoryCircleAnalysis__barLabelDivider',
    )
    const valueElement = groupElement.querySelector<SVGTextElement>(
      'text.InventoryCircleAnalysis__barLabelValue',
    )

    const dividerY = nameBox.height + OVERLAY_LABEL_VALUE_GAP
    const valueY = dividerY + OVERLAY_LABEL_LINE_HEIGHT

    if (dividerElement) {
      dividerElement.setAttribute('y1', String(dividerY))
      dividerElement.setAttribute('y2', String(dividerY))
    }
    if (valueElement) {
      valueElement.setAttribute('y', String(valueY))
    }
  } as unknown as ValueFn<BaseType, AggregationEntry, void>)

  labelGroupsMerge
    .select('text.InventoryCircleAnalysis__barLabelValue')
    .text((entry: AggregationEntry) => numberFormat(entry.count))

  labelGroups.exit().remove()

  barGroups.exit().remove()
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- overlay disabled
function clearOverlayChart() {
  if (!overlaySvg) {
    return
  }
  const svgSelection = select(overlaySvg)
  svgSelection.selectAll('*').remove()
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- overlay disabled
function scheduleOverlayUpdate() {
  // Overlay disabled - bars are displayed in panel instead
  // No need to update overlay chart on map
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- overlay disabled
function attachOverlay() {
  // Overlay disabled - bars are displayed in panel instead
  // const map = mapStore.myMap?.map
  // if (!map || overlayContainer) {
  //   return
  // }
  // overlayContainer = document.createElement('div')
  // overlayContainer.className = 'InventoryCircleAnalysis__overlay'
  // overlaySvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  // overlaySvg.classList.add('InventoryCircleAnalysis__overlaySvg')
  // overlayContainer.appendChild(overlaySvg)
  // map.getContainer().appendChild(overlayContainer)
}

function detachOverlay() {
  if (overlayContainer && overlayContainer.parentElement) {
    overlayContainer.parentElement.removeChild(overlayContainer)
  }
  overlayContainer = null
  overlaySvg = null
  if (overlayAnimationFrame !== null) {
    cancelAnimationFrame(overlayAnimationFrame)
    overlayAnimationFrame = null
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- overlay disabled
function setOverlayVisibility(isVisible: boolean) {
  if (!overlayContainer) {
    return
  }
  overlayContainer.style.opacity = isVisible ? '1' : '0'
}

function activate() {
  const map = mapStore.myMap?.map
  console.assert(
    Boolean(map),
    '[InventoryCircleAnalysis] Map is required to activate inventory circle',
  )
  if (!map) {
    return
  }

  // Initialize circle center for radius calculations, but don't filter points by circle
  if (!circleCenter.value) {
    circleCenter.value = map.getCenter()
  }
  updateScaleDenominator()
  ensureVectorSource()
  ensureQueryLayer()
  updateCircleLayers()
  attachIdleListener()
  attachMoveListener()
  attachZoomListener()
  // Kicked off as early as possible so the map-registered picto/badge images (which warm this
  // same cache themselves, see ensurePictoImages324/ensureMarkerImages322/329) are more likely to
  // build with the final admin-configured appearance on their first pass. Bumps
  // typeOuvrageVisualsVersion once resolved so the legend/gauge computeds (Vue-reactive, so they
  // don't get a second chance the way the map images do) pick it up even if they rendered first.
  void prefetchTypeOuvrageVisuals().then(() => {
    typeOuvrageVisualsVersion.value++
  })
  // Always update aggregation - zoom restriction removed, no circular filter
  void updateAggregation()
}

function deactivate() {
  clearAnalysisOverlay()
  detachCircleDragHandlers()
  detachIdleListener()
  detachMoveListener()
  detachZoomListener()
  detachResizeHandlers()
}

function clearAnalysisOverlay() {
  removeCircleLayers()
  detachDragMarker()
  detachOverlay()
  aggregationMap.value = {}
}

function detachResizeHandlers() {
  const map = mapStore.myMap?.map
  if (!map) {
    return
  }
  if (mapResizeMoveHandler) {
    map.off('mousemove', mapResizeMoveHandler)
    mapResizeMoveHandler = null
  }
  if (mapResizeUpHandler) {
    map.off('mouseup', mapResizeUpHandler)
    mapResizeUpHandler = null
  }
  isResizingCircle.value = false
}

watch(
  () => ({
    isActive: props.isActive,
    isZoomAllowed: isZoomAllowed.value,
  }),
  ({ isActive }) => {
    if (!isActive) {
      deactivate()
      return
    }
    updateScaleDenominator()
    attachMoveListener()
    attachZoomListener()
    attachIdleListener()

    // Always activate - zoom restriction removed
    activate()
  },
  { immediate: true },
)

watch(radiusMetersValue, () => {
  if (!props.isActive) {
    return
  }
  updateCircleLayers()
  void updateAggregation()
  // Overlay disabled - no need to clear or update
})

watch(
  selectedMaintenance329Buckets,
  () => {
    if (!props.isActive || !isIndicator329.value) {
      return
    }
    void updateAggregation()
  },
  { deep: true },
)

onUnmounted(() => {
  const indicatorId = Number(props.indicatorKey)
  const isStillVisibleInLegend =
    Number.isFinite(indicatorId) &&
    mapStore.visibleIndicatorEntries.some((entry) => entry.id === indicatorId)
  const isStillActive = props.isActive

  if (isStillActive && isStillVisibleInLegend) {
    // The domain panel can unmount this component when closing UI sections.
    // Keep map layers/sources visible and only detach runtime listeners.
    detachCircleDragHandlers()
    detachIdleListener()
    detachMoveListener()
    detachZoomListener()
    detachResizeHandlers()
    detachOverlay()
    return
  }

  // Eye toggled off (isActive=false) or legend no longer contains the indicator:
  // perform full cleanup so the analysis truly disappears.
  deactivate()
})
</script>

<style lang="scss">
.InventoryCircleAnalysis {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  --inventory-drag-size: 14px;
  --inventory-drag-border: 2px;
  --overlay-bar-hover-color: #a44;
  --overlay-value-color: #222;
  --overlay-label-color: #fff;
  --overlay-value-font-size: 11px;
  --overlay-label-font-size: 10px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  &__title {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.65);
  }

  &__value {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgb(var(--v-theme-main-blue));
  }

  &__slider {
    width: 100%;
  }

  &__rangeInput {
    width: 100%;
    cursor: pointer;
    accent-color: rgb(var(--v-theme-main-blue));
  }

  &__zoomNotice {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
  }

  &__bars {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  &__filterHeader {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-top: 0.25rem;
  }

  &__selectAllToggle {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.45rem 0.7rem;
    border: 1px solid rgba(20, 77, 126, 0.14);
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(20, 77, 126, 0.06), rgba(20, 77, 126, 0.1));
    cursor: pointer;
  }

  &__selectAllLabel {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    color: rgba(0, 0, 0, 0.78);
    text-transform: uppercase;
  }

  &__typeLegend {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  &__typeLegendTitle {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.65);
  }

  &__typeLegendGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.6rem 0.7rem;
  }

  &__statusLegend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1rem;
  }

  &__statusLegendItem {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  &__statusLegendDot {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
  }

  &__statusLegendLabel {
    font-size: 0.72rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.8);
  }

  &__typeLegendItem {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
    min-width: 0;
    padding: 0.4rem 0.5rem;
    border-radius: 0.5rem;
    background: rgba(0, 0, 0, 0.03);
  }

  &__typeLegendHeader {
    font-size: 0.72rem;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.82);
    line-height: 1.1;
  }

  &__typeLegendStates {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem 0.65rem;
  }

  &__typeLegendState {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    min-width: 0;
  }

  &__typeLegendIcon {
    width: 1.35rem;
    height: 1.35rem;
    flex-shrink: 0;
  }

  &__typeLegendStateLabel {
    font-size: 0.68rem;
    line-height: 1.15;
    color: rgba(0, 0, 0, 0.78);
    min-width: 0;
  }

  &__barItem {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__barLabel {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
    gap: 0.35rem;
  }

  &__barLabelIcon {
    width: 1.15rem;
    height: 1.15rem;
    margin-right: 0.25rem;
    flex-shrink: 0;
  }

  &__classToggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 1.15rem;
    height: 1.15rem;
    margin-right: 0.1rem;
    cursor: pointer;
  }

  &__classToggleInput {
    appearance: none;
    -webkit-appearance: none;
    width: 1rem;
    height: 1rem;
    border: 1.5px solid rgba(20, 77, 126, 0.45);
    border-radius: 0.28rem;
    background: #ffffff;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 1px 2px rgba(0, 0, 0, 0.06);
    cursor: pointer;
    position: relative;
    transition:
      background-color 0.18s ease,
      border-color 0.18s ease,
      box-shadow 0.18s ease,
      transform 0.18s ease;

    &:hover {
      border-color: rgba(20, 77, 126, 0.72);
      box-shadow: 0 0 0 3px rgba(20, 77, 126, 0.08);
    }

    &:checked {
      background: rgb(var(--v-theme-main-blue));
      border-color: rgb(var(--v-theme-main-blue));
    }

    &:checked::after {
      content: '';
      position: absolute;
      left: 0.29rem;
      top: 0.08rem;
      width: 0.22rem;
      height: 0.48rem;
      border: solid #ffffff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(20, 77, 126, 0.18);
    }
  }

  &__barLabelName {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.8);
    text-transform: uppercase;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__barLabelValue {
    font-weight: 600;
    color: rgb(var(--v-theme-main-blue));
    margin-left: 0.5rem;
    flex-shrink: 0;
  }

  &__barContainer {
    width: 100%;
    height: 20px;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    overflow: hidden;
  }

  &__bar {
    height: 100%;
    border-radius: 4px;
    transition: width 0.2s ease;
  }

  &__barLabelName {
    fill: rgba(0, 0, 0, 0.9);
    font-size: 11px;
    font-weight: 700;
    pointer-events: none;
  }

  &__barLabelDivider {
    stroke: rgba(0, 0, 0, 0.6);
    stroke-width: 1;
    pointer-events: none;
  }

  &__barLabelValue {
    fill: rgba(0, 0, 0, 0.9);
    font-size: 11px;
    font-weight: 600;
    pointer-events: none;
  }

  &__anchorIcon {
    fill: white;
    font-size: 12px;
    font-weight: 700;
    pointer-events: none;
  }

  &__dragMarker {
    width: var(--inventory-drag-size);
    height: var(--inventory-drag-size);
    border-radius: 4px;
    border: var(--inventory-drag-border) solid rgba(0, 0, 0, 0.6);
    background-color: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    cursor: grab;
  }

  &__dragMarker:active {
    cursor: grabbing;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 2;
  }

  &__overlaySvg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  &__radialWidget {
    pointer-events: none;
    font-family: 'Helvetica Neue', Arial, sans-serif;
    font-size: 12px;
  }

  &__overlayBar {
    pointer-events: auto;
    transition: fill 0.3s;
  }

  &__barGroup:hover &__overlayBar {
    fill: var(--overlay-bar-hover-color);
  }

  &__overlayLabel {
    fill: var(--overlay-label-color);
    font-size: var(--overlay-label-font-size);
    font-weight: 600;
    text-transform: uppercase;
    user-select: none;
    pointer-events: none;
  }

  &__labelPath {
    fill: none;
    stroke: none;
  }
}
</style>
